import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { DEFAULT_SCREENING_SENSITIVITY } from '../../utils/screeningDefaults';
import { useUserInfo } from '../../contexts/UserContext';
import { useFlashMessage } from '../FlashMessage';
import { dashboardService } from '../../services/dashboardService';
import KYCUploadModal from './KYCUploadModal';
import ConfirmationPopup from '../ConfirmationPopup';
import { getErrorMessage } from '../../utils/errorMessages';
import { getApprovalBadge } from '../../utils/crmStatusMapper';
import { confirmName, lookupNames } from '../../services/documentNamingService';
import SuggestedName from '../DocumentNaming/SuggestedName';
import useSuggestionPolling from '../DocumentNaming/useSuggestionPolling';
import './DilisenseStyles.css';
import '../CRMProfileScreen.css';
import { CRMTabHero, CRMSectionHeader, TabIcons } from './CRMTabUI';
import CRMKYCOnboardingSection from './CRMKYCOnboardingSection';

const CRMKYCComplianceTab = ({ profileData, onDataChange, onServerStateChange, isNewProfile, isKycApprover, onFillFromKycDocs }) => {
    const { userInfo } = useUserInfo();
    const { showError, showSuccess } = useFlashMessage();
    
    const [formData, setFormData] = useState({
        kyc_status_current: 'Pending',
        sanction_screening_result: {},
        last_kyc_update_date: '',
        ultimate_beneficial_owner_info: {},
        manual_ultimate_beneficial_owners: [],
        politically_exposed_persons_data: {},
        aml_risk_level_current: 'Medium',
        
        // Humpert & Kneer sanctions screening fields
        hk_sanction_screening_data: {},
        hk_last_screening_date: null,
        hk_screening_sensitivity: DEFAULT_SCREENING_SENSITIVITY,
        hk_suspect_flag: false,
        hk_highest_score: null,
        hk_screening_history: [],
        hk_matched_lists: [],
        hk_individual_screening_data: {},
        hk_individual_last_screening: null,
        hk_individual_suspect_flag: false,
        
        ...profileData
    });

    // Simple UI State for the 4 main operations
    const [isLoading, setIsLoading] = useState({
        entitySearch: false,
        entityReport: false,
        individualSearch: false,
        individualReport: false
    });
    
    // Simple search states
    const [entityName, setEntityName] = useState('');
    const [entityAddress, setEntityAddress] = useState('');
    const [sensitivity, setSensitivity] = useState(DEFAULT_SCREENING_SENSITIVITY);
    const [individualName, setIndividualName] = useState('');
    const [individualAddress, setIndividualAddress] = useState('');
    const [uboScreeningLoading, setUboScreeningLoading] = useState({});
    const [uboReportLoading, setUboReportLoading] = useState({});
    const [entityResults, setEntityResults] = useState(null);
    const [individualResults, setIndividualResults] = useState(null);
    
    // Track if results are from stored data vs fresh searches
    const [entityResultsFromStorage, setEntityResultsFromStorage] = useState(false);
    const [individualResultsFromStorage, setIndividualResultsFromStorage] = useState(false);
    


    // KYC Document management state
    const [kycDocuments, setKycDocuments] = useState([]);
    const [kycDisplayNames, setKycDisplayNames] = useState({});
    useSuggestionPolling(kycDisplayNames, setKycDisplayNames, 'kyc');
    const [loadingKycDocuments, setLoadingKycDocuments] = useState(false);
    const [kycDocumentError, setKycDocumentError] = useState(null);
    const [showKycDocuments, setShowKycDocuments] = useState(false);
    const [showKycUploadModal, setShowKycUploadModal] = useState(false);
    const [uploadingKyc, setUploadingKyc] = useState(false);
    const [groupedKycDocuments, setGroupedKycDocuments] = useState({});
    
    // Swiss SECO SESAM sanctions-check state
    const [secoChecks, setSecoChecks] = useState([]);
    const [loadingSecoChecks, setLoadingSecoChecks] = useState(false);
    const [recordingSecoCheck, setRecordingSecoCheck] = useState(false);
    const [secoContext, setSecoContext] = useState('');
    const [secoNotes, setSecoNotes] = useState('');
    const [secoAnnexI, setSecoAnnexI] = useState(false);
    const [secoAnnexII, setSecoAnnexII] = useState(false);

    // In-house SECO list screening state (fuzzy match vs imported SECO list)
    const [secoScreening, setSecoScreening] = useState(null); // {latest, history, reviews, current_list_version_date, list_imported}
    const [loadingSecoScreening, setLoadingSecoScreening] = useState(false);
    const [runningSecoScreening, setRunningSecoScreening] = useState(false);

    // Delete confirmation state
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    
    // Duplicate detection state (similar to HAMSTIR Builder)
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateFileInfo, setDuplicateFileInfo] = useState(null);
    
    // Current user info for employee attribution
    const [currentUserInfo, setCurrentUserInfo] = useState(null);
    
    // Employee information state for document metadata
    const [employeeInfoCache, setEmployeeInfoCache] = useState({}); // Cache for employee info by user_id

    // Report upload confirmation state
    const [showReportUploadConfirmation, setShowReportUploadConfirmation] = useState(false);
    const [pendingReportUpload, setPendingReportUpload] = useState(null);
    const [uploadingReport, setUploadingReport] = useState(false);

    // KYC Document Types (A3) — document sub-types, not AML process stages.
    // These strings must match KYC_DOCUMENT_SUBTYPE_CODES in the backend's
    // document_naming module_config so the AI naming layer resolves the short
    // code (e.g. KYC-BANK) into the display name. The Document Hub's Bank
    // Confirmation bucket mirrors any document resolved to KYC-BANK.
    const KYC_DOCUMENT_TYPES = [
        'Bank Reference / Confirmation (KYC-Bank)',
        'Certificate of Incorporation (KYC-COI)',
        'Articles of Association / Memorandum (KYC-AAM)',
        'VAT Registration (KYC-VAT)',
        'Ultimate Beneficial Owners (KYC-UBO)',
        'Certificate of Compliance (KYC-Compliance)',
        'Audited Financials (KYC-Audit)',
        'Trading Certificate (KYC-TC)',
        'Authorised Signatory List (KYC-Sign)',
        'UBO Identity Documents (KYC-UBO ID)',
        'Credit Reference / Rating (KYC-Credit)',
        'Financial Statements (KYC-Fin)',
        'Other (KYC)'
    ];

    useEffect(() => {
        if (profileData) {
            // Only update formData if it's a completely new profile or if the current formData is empty
            // This prevents overriding user changes with stale data
            setFormData(prevFormData => {
                // If this is a completely new profile or first load
                if (isNewProfile || !prevFormData.kyc_status_current) {
                    // Use existing UBOs or empty array if none exist
                    let ubos = profileData.manual_ultimate_beneficial_owners || [];
                    
                    return {
                        kyc_status_current: profileData.kyc_status_current || 'Pending',
                        sanction_screening_result: profileData.sanction_screening_result || {},
                        last_kyc_update_date: profileData.last_kyc_update_date || '',
                        ultimate_beneficial_owner_info: profileData.ultimate_beneficial_owner_info || {},
                        manual_ultimate_beneficial_owners: ubos,
                        politically_exposed_persons_data: profileData.politically_exposed_persons_data || {},
                        aml_risk_level_current: profileData.aml_risk_level_current || 'Medium',
                        hk_sanction_screening_data: profileData.hk_sanction_screening_data || {},
                        hk_last_screening_date: profileData.hk_last_screening_date || null,
                        hk_screening_sensitivity: profileData.hk_screening_sensitivity || DEFAULT_SCREENING_SENSITIVITY,
                        hk_suspect_flag: profileData.hk_suspect_flag || false,
                        hk_highest_score: profileData.hk_highest_score || null,
                        hk_screening_history: profileData.hk_screening_history || [],
                        hk_matched_lists: profileData.hk_matched_lists || [],
                        hk_individual_screening_data: profileData.hk_individual_screening_data || {},
                        hk_individual_last_screening: profileData.hk_individual_last_screening || null,
                        hk_individual_suspect_flag: profileData.hk_individual_suspect_flag || false,
                        ...profileData
                    };
                }
                // Return previous formData to preserve user changes
                return prevFormData;
            });
            
            // Auto-populate entity name from Partner Profile if available and not already set
            if (profileData.legal_entity_name && !entityName) {
                console.log('📝 Auto-populating entity name from Partner Profile:', profileData.legal_entity_name);
                setEntityName(profileData.legal_entity_name);
            }

            // Auto-populate entity address from Partner Profile head office address if available and not already set
            if (profileData.head_office_address_data?.formatted_address && !entityAddress) {
                console.log('📝 Auto-populating entity address from Partner Profile:', profileData.head_office_address_data.formatted_address);
                setEntityAddress(profileData.head_office_address_data.formatted_address);
            }

            // Auto-display stored search results when returning to profile
            // Only display if we have stored results and current results are empty
            if (!isNewProfile && profileData.id) {
                // Display stored entity screening results
                if (profileData.hk_sanction_screening_data && 
                    Object.keys(profileData.hk_sanction_screening_data).length > 0 && 
                    !entityResults) {
                    console.log('📋 Auto-displaying stored entity screening results');
                    setEntityResults(profileData.hk_sanction_screening_data);
                    setEntityResultsFromStorage(true); // Mark as from storage
                }

                // Display stored individual screening results
                if (profileData.hk_individual_screening_data && 
                    Object.keys(profileData.hk_individual_screening_data).length > 0 && 
                    !individualResults) {
                    console.log('📋 Auto-displaying stored individual screening results');
                    setIndividualResults(profileData.hk_individual_screening_data);
                    setIndividualResultsFromStorage(true); // Mark as from storage
                }
            }
        }
    }, [profileData, isNewProfile, entityName, entityAddress, entityResults, individualResults]);



    // Get current user information for employee_id
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const userInfoData = await dashboardService.getUserInfo();
                setCurrentUserInfo(userInfoData);
            } catch (error) {
                console.error('Error fetching user info:', error);
                setCurrentUserInfo(userInfo);
            }
        };

        if (!currentUserInfo && userInfo?.user_id) {
            setCurrentUserInfo(userInfo);
        } else if (!currentUserInfo) {
            fetchUserInfo();
        }
    }, [userInfo, currentUserInfo]);

    // Auto-fetch KYC documents when tab opens and profile is available
    useEffect(() => {
        if (profileData?.id) {
            console.log('Auto-fetching KYC documents for partner:', profileData.id);
            fetchKycDocuments();
            setShowKycDocuments(true);
        }
    }, [profileData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-fetch SECO SESAM sanctions-check history when profile is available
    useEffect(() => {
        if (profileData?.id) {
            fetchSecoChecks();
            fetchSecoScreening();
        }
    }, [profileData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Manual "tick KYC as done" for a partner handled via a manual (no-portal)
    // process. Both rules are enforced server-side: who may press it, and whether
    // a document is required (waived while a KYCGateBypass window is open).
    const [markingKycApproved, setMarkingKycApproved] = useState(false);

    const handleMarkKycApproved = async () => {
        if (!profileData?.id) return;
        // Approving with nothing on file is allowed during a bypass window, but it
        // must never be a quiet click — the confirm names what is missing.
        const confirmed = window.confirm(
            kycDocuments.length === 0
                ? "This partner has NO KYC documents on file.\n\nMark KYC as approved "
                  + "anyway? Business (recaps) will be unblocked and the approval will "
                  + "be recorded as granted without documents."
                : "Mark this partner's KYC as approved? This lets business (recaps) continue."
        );
        if (!confirmed) return;
        setMarkingKycApproved(true);
        try {
            const response = await axiosInstance.post(
                `/api/crm/partners/${profileData.id}/mark-kyc-approved/`,
                { document_count: kycDocuments.length }
            );
            // Adopt what the SERVER says the record now is, rather than patching
            // the one field we happen to name here. The endpoint returns both
            // halves; before this it returned approval_status and the screen
            // discarded it, so the "Profile Approval Status" box below kept
            // reading Pending until a full page reload.
            const serverState = {
                kyc_status_current: response.data?.kyc_status_current,
                approval_status: response.data?.approval_status,
            };
            setFormData(prev => ({
                ...prev,
                ...Object.fromEntries(
                    Object.entries(serverState).filter(([, v]) => v !== undefined)
                ),
            }));
            // Push it up too: the approval badge renders off the parent's
            // profileData, which nothing else here can refresh.
            if (typeof onServerStateChange === 'function') {
                onServerStateChange(serverState);
            }

            const minted = response.data?.assigned_numbers || {};
            const mintedText = Object.keys(minted).length
                ? ' Assigned ' + Object.entries(minted)
                    .map(([k, v]) => `${k.replace('_number', '')} number ${v}`)
                    .join(' and ') + '.'
                : '';
            showSuccess(
                (response.data?.approved_without_documents
                    ? 'KYC approved with NO documents on file — recorded in the audit trail.'
                    : (response.data?.message || 'KYC marked as approved.')) + mintedText
            );
        } catch (error) {
            showError(
                error.response?.data?.error
                || 'Failed to mark KYC as approved. Please try again.'
            );
        } finally {
            setMarkingKycApproved(false);
        }
    };

    const fetchSecoScreening = async () => {
        if (!profileData?.id) return;
        setLoadingSecoScreening(true);
        try {
            const response = await axiosInstance.get(
                `/api/crm/profiles/${profileData.id}/seco-screening/`
            );
            setSecoScreening(response.data || null);
        } catch (error) {
            console.error('Failed to load SECO screening:', error);
        } finally {
            setLoadingSecoScreening(false);
        }
    };

    const handleRunSecoScreening = async () => {
        if (!profileData?.id) {
            showError('Please save the partner profile before screening it against the SECO list.');
            return;
        }
        setRunningSecoScreening(true);
        try {
            const response = await axiosInstance.post(
                `/api/crm/profiles/${profileData.id}/seco-screening/screen/`
            );
            setSecoScreening(response.data || null);
            showSuccess('SECO screening complete.');
        } catch (error) {
            showError(getErrorMessage(error, 'Failed to run SECO screening. Please try again.'));
        } finally {
            setRunningSecoScreening(false);
        }
    };

    const handleReviewSecoHit = async (hit, decision) => {
        if (!profileData?.id) return;
        const verb = decision === 'dismissed' ? 'dismiss' : 'escalate';
        const note = window.prompt(
            `Add a note to ${verb} the potential match on "${hit.matched_name}" (required):`
        );
        if (note === null) return; // cancelled
        if (!note.trim()) {
            showError('A note is required to review a potential hit.');
            return;
        }
        try {
            await axiosInstance.post(
                `/api/crm/profiles/${profileData.id}/seco-screening/review/`,
                {
                    target_ssid: hit.target_ssid,
                    decision,
                    note: note.trim(),
                    screening_id: secoScreening?.latest?.id,
                }
            );
            showSuccess(decision === 'dismissed'
                ? 'Potential hit dismissed.'
                : 'Potential hit escalated to compliance.');
            fetchSecoScreening();
        } catch (error) {
            showError(getErrorMessage(error, 'Failed to record the review. Please try again.'));
        }
    };

    const fetchSecoChecks = async () => {
        if (!profileData?.id) return;
        setLoadingSecoChecks(true);
        try {
            const response = await axiosInstance.get(
                `/api/crm/profiles/${profileData.id}/seco-checks/`
            );
            setSecoChecks(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Failed to load SECO SESAM checks:', error);
        } finally {
            setLoadingSecoChecks(false);
        }
    };

    const handleRecordSecoCheck = async () => {
        if (!profileData?.id) {
            showError('Please save the partner profile before recording a SECO SESAM check.');
            return;
        }
        if (!secoAnnexI || !secoAnnexII) {
            showError('Both Annex I and Annex II must be checked to record a SECO SESAM verification.');
            return;
        }
        setRecordingSecoCheck(true);
        try {
            const response = await axiosInstance.post(
                `/api/crm/profiles/${profileData.id}/seco-checks/`,
                {
                    annex_i: secoAnnexI,
                    annex_ii: secoAnnexII,
                    context: secoContext,
                    notes: secoNotes,
                }
            );
            setSecoChecks(prev => [response.data, ...prev]);
            setSecoContext('');
            setSecoNotes('');
            setSecoAnnexI(false);
            setSecoAnnexII(false);
            showSuccess('SECO SESAM check recorded.');
        } catch (error) {
            showError(getErrorMessage(error, 'Failed to record SECO SESAM check. Please try again.'));
        } finally {
            setRecordingSecoCheck(false);
        }
    };

    const handleInputChange = (field, value) => {
        console.log(`🔄 KYC FIELD CHANGE: ${field} = ${value}`);
        
        // Add specific logging for screening data storage
        if (field === 'dilisense_entity_screening_data') {
            console.log(`📊 ENTITY SCREENING DATA STORED:`, value);
        } else if (field === 'dilisense_individual_screenings') {
            console.log(`📊 INDIVIDUAL SCREENING DATA STORED:`, value);
        }
        
        const updatedData = {
            ...formData,
            [field]: value
        };
        setFormData(updatedData);
        console.log(`📋 Updated formData for ${field}:`, updatedData[field]);
        onDataChange(field, updatedData[field]);
        console.log(`📤 Called onDataChange for ${field} with value:`, updatedData[field]);
    };

    const updateLoadingState = (key, value) => {
        setIsLoading(prev => ({ ...prev, [key]: value }));
    };

    // Ultimate Beneficial Owner Management Functions
    const addUbo = () => {
        const newUbo = {
            ultimate_beneficial_owner_name: '',
            ultimate_beneficial_owner_address: '',
            ultimate_beneficial_owner_sensitivity: DEFAULT_SCREENING_SENSITIVITY,
            ultimate_beneficial_owner_details: ''
        };
        const updatedUbos = [...(formData.manual_ultimate_beneficial_owners || []), newUbo];
        handleInputChange('manual_ultimate_beneficial_owners', updatedUbos);
    };

    const removeUbo = (index) => {
        const currentUbos = formData.manual_ultimate_beneficial_owners || [];
        const updatedUbos = currentUbos.filter((_, i) => i !== index);
        handleInputChange('manual_ultimate_beneficial_owners', updatedUbos);
    };

    const updateUboField = (index, field, value) => {
        const updatedUbos = [...(formData.manual_ultimate_beneficial_owners || [])];
        if (updatedUbos[index]) {
            updatedUbos[index] = { ...updatedUbos[index], [field]: value };
            handleInputChange('manual_ultimate_beneficial_owners', updatedUbos);
        }
    };

    // Run sanctions screening for a single UBO using the H&K endpoint.
    // Result is stored on the UBO record so it persists with the profile (manual_ultimate_beneficial_owners is a JSONField).
    const screenUbo = async (index) => {
        const ubo = formData.manual_ultimate_beneficial_owners?.[index];
        if (!ubo) return;

        const name = (ubo.ultimate_beneficial_owner_name || '').trim();
        const address = (ubo.ultimate_beneficial_owner_address || '').trim();
        const uboSensitivity = ubo.ultimate_beneficial_owner_sensitivity ?? DEFAULT_SCREENING_SENSITIVITY;

        if (!name) {
            showError('UBO name is required for sanctions screening');
            return;
        }
        if (!address) {
            showError('UBO address is required for sanctions screening');
            return;
        }

        setUboScreeningLoading(prev => ({ ...prev, [index]: true }));

        try {
            const response = await axiosInstance.post('/api/crm/profiles/sanction-check/', {
                name,
                address,
                sensitivity: uboSensitivity,
                entity_type: 'individual'
            });

            if (response.data.success) {
                const screeningData = response.data.data || {};
                updateUboField(index, 'sanction_screening_result', {
                    ...screeningData,
                    screening_date: response.data.screening_date,
                    sensitivity: response.data.sensitivity
                });

                const status = screeningData.suspect === 1 ? 'SUSPICIOUS' : 'CLEAR';
                const score = screeningData.score || 0;
                showSuccess(`UBO sanctions check completed for "${name}" - ${status} (Score: ${score})`);
            } else {
                throw new Error(response.data.error || 'UBO sanctions screening failed');
            }
        } catch (error) {
            console.error('UBO sanctions screening error:', error);
            showError(getErrorMessage(error, 'UBO sanctions screening failed. Please try again.'));
        } finally {
            setUboScreeningLoading(prev => ({ ...prev, [index]: false }));
        }
    };

    // Generate a PDF sanctions report for a single UBO and trigger the DocuWare upload confirmation popup.
    // Mirrors handleEntityReport but sources name/address/sensitivity from the UBO row.
    const generateUboReport = async (index) => {
        const ubo = formData.manual_ultimate_beneficial_owners?.[index];
        if (!ubo) return;

        const name = (ubo.ultimate_beneficial_owner_name || '').trim();
        const address = (ubo.ultimate_beneficial_owner_address || '').trim();
        const uboSensitivity = ubo.ultimate_beneficial_owner_sensitivity ?? DEFAULT_SCREENING_SENSITIVITY;

        if (!name) {
            showError('UBO name is required for sanctions report generation');
            return;
        }
        if (!address) {
            showError('UBO address is required for sanctions report generation');
            return;
        }
        if (!profileData?.id) {
            showError('Please save the partner profile first before generating reports with upload option');
            return;
        }

        setUboReportLoading(prev => ({ ...prev, [index]: true }));

        try {
            const response = await axiosInstance.post('/api/crm/profiles/sanction-report/', {
                name,
                address,
                sensitivity: uboSensitivity,
                partner_name: profileData?.legal_entity_name || name
            });

            if (response.data.success && response.data.pdf_base64) {
                setPendingReportUpload({
                    type: 'ubo_sanctions',
                    fileName: `${name}_UBO_sanctions_report.pdf`,
                    pdfBase64: response.data.pdf_base64,
                    entityName: name,
                    kycDocumentType: 'Sanctions Screening'
                });
                setShowReportUploadConfirmation(true);
            } else {
                throw new Error(response.data.error || 'UBO sanctions report generation failed');
            }
        } catch (error) {
            console.error('UBO sanctions report error:', error);
            showError(getErrorMessage(error, 'UBO sanctions report generation failed. Please try again.'));
        } finally {
            setUboReportLoading(prev => ({ ...prev, [index]: false }));
        }
    };

    // Helper function to manually populate entity data from Partner Profile
    const loadPartnerDataToSanctionCheck = () => {
        if (profileData?.legal_entity_name) {
            setEntityName(profileData.legal_entity_name);
            console.log('✅ Manually loaded entity name from Partner Profile');
        }
        if (profileData?.head_office_address_data?.formatted_address) {
            setEntityAddress(profileData.head_office_address_data.formatted_address);
            console.log('✅ Manually loaded entity address from Partner Profile');
        }
        
        if (profileData?.legal_entity_name || profileData?.head_office_address_data?.formatted_address) {
            showSuccess('Partner data loaded into sanction check fields');
        } else {
            showError('No partner data available. Please fill in Partner Profile tab first.');
        }
    };

    // 1. Sanctions Screening using search_all
    const handleEntitySearch = async () => {
        if (!entityName.trim()) {
            showError('Entity name is required for sanctions screening');
            return;
        }

        if (!entityAddress.trim()) {
            showError('Address is required for sanctions screening');
            return;
        }

        updateLoadingState('entitySearch', true);
        setEntityResults(null);
        setEntityResultsFromStorage(false); // Reset from storage flag

        try {
            console.log('🔍 Starting sanctions screening for:', entityName.trim(), 'with sensitivity:', sensitivity);
            const response = await axiosInstance.post('/api/crm/profiles/sanction-check/', {
                name: entityName.trim(),
                address: entityAddress.trim(),
                sensitivity: sensitivity
            });

            if (response.data.success) {
                const searchResults = response.data.data;
                
                // Display results immediately
                setEntityResults(searchResults);
                
                // Store results in formData for saving with profile
                console.log('💾 Storing sanctions screening results in formData for profile save');
                handleInputChange('hk_sanction_screening_data', searchResults);
                
                const suspect = searchResults?.suspect ? 'SUSPICIOUS' : 'CLEAR';
                const score = searchResults?.score || 0;
                showSuccess(`Sanctions screening completed for "${entityName}" - Status: ${suspect} (Score: ${score})`);
                console.log('✅ Sanctions screening completed successfully');
            } else {
                throw new Error(response.data.error || 'Sanctions screening failed');
            }
        } catch (error) {
            console.error('❌ Sanctions screening error:', error);
            showError(getErrorMessage(error, 'KYC operation failed. Please try again.'));
        } finally {
            updateLoadingState('entitySearch', false);
        }
    };

    // 2. Generate Sanctions Report (PDF)
    const handleEntityReport = async () => {
        if (!entityName.trim()) {
            showError('Entity name is required for sanctions report generation');
            return;
        }

        if (!entityAddress.trim()) {
            showError('Address is required for sanctions report generation');
            return;
        }

        // Check if profile is saved for upload functionality
        if (!profileData?.id) {
            showError('Please save the partner profile first before generating reports with upload option');
            return;
        }

        updateLoadingState('entityReport', true);

        try {
            console.log('📄 Generating sanctions report for:', entityName.trim());
            const response = await axiosInstance.post('/api/crm/profiles/sanction-report/', {
                name: entityName.trim(),
                address: entityAddress.trim(),
                sensitivity: sensitivity,
                partner_name: profileData?.legal_entity_name || entityName.trim()
            });

            if (response.data.success && response.data.pdf_base64) {
                console.log('✅ Sanctions report generated successfully');
                
                // Show upload confirmation popup
                setPendingReportUpload({
                    type: 'sanctions',
                    fileName: `${entityName}_sanctions_report.pdf`,
                    pdfBase64: response.data.pdf_base64,
                    entityName: entityName.trim(),
                    kycDocumentType: 'Sanctions Screening'
                });
                setShowReportUploadConfirmation(true);
                
            } else {
                throw new Error(response.data.error || 'Sanctions report generation failed');
            }
        } catch (error) {
            console.error('❌ Sanctions report error:', error);
            showError(getErrorMessage(error, 'KYC operation failed. Please try again.'));
        } finally {
            updateLoadingState('entityReport', false);
        }
    };

    // 3. PEP Check using the same Humpert & Kneer sanctions API
    const handleIndividualSearch = async () => {
        if (!individualName.trim()) {
            showError('Individual name is required for PEP check');
            return;
        }

        if (!individualAddress.trim()) {
            showError('Address is required for PEP check');
            return;
        }

        updateLoadingState('individualSearch', true);
        setIndividualResults(null);
        setIndividualResultsFromStorage(false); // Reset from storage flag

        try {
            console.log('👤 Starting PEP/sanctions check for:', individualName.trim(), 'with sensitivity:', sensitivity);
            const response = await axiosInstance.post('/api/crm/profiles/sanction-check/', {
                name: individualName.trim(),
                address: individualAddress.trim(),
                sensitivity: sensitivity
            });

            if (response.data.success) {
                const searchResults = response.data.data;
                
                // Display results immediately
                setIndividualResults(searchResults);
                
                // Store results in formData for saving with profile
                console.log('💾 Storing individual screening results in formData for profile save');
                handleInputChange('hk_individual_screening_data', searchResults);
                
                const suspect = searchResults?.suspect ? 'SUSPICIOUS' : 'CLEAR';
                const score = searchResults?.score || 0;
                showSuccess(`PEP check completed for "${individualName}" - Status: ${suspect} (Score: ${score})`);
                console.log('✅ PEP check completed successfully');
            } else {
                throw new Error(response.data.error || 'PEP check failed');
            }
        } catch (error) {
            console.error('❌ PEP check error:', error);
            showError(getErrorMessage(error, 'KYC operation failed. Please try again.'));
        } finally {
            updateLoadingState('individualSearch', false);
        }
    };

    // 4. Generate PEP Report (PDF)
    const handleIndividualReport = async () => {
        if (!individualName.trim()) {
            showError('Individual name is required for PEP report generation');
            return;
        }

        if (!individualAddress.trim()) {
            showError('Address is required for PEP report generation');
            return;
        }

        // Check if profile is saved for upload functionality
        if (!profileData?.id) {
            showError('Please save the partner profile first before generating reports with upload option');
            return;
        }

        updateLoadingState('individualReport', true);

        try {
            console.log('📄 Generating PEP report for:', individualName.trim());
            const response = await axiosInstance.post('/api/crm/profiles/sanction-report/', {
                name: individualName.trim(),
                address: individualAddress.trim(),
                sensitivity: sensitivity,
                partner_name: profileData?.legal_entity_name || individualName.trim()
            });

            if (response.data.success && response.data.pdf_base64) {
                console.log('✅ PEP report generated successfully');
                
                // Show upload confirmation popup
                setPendingReportUpload({
                    type: 'pep',
                    fileName: `${individualName}_pep_report.pdf`,
                    pdfBase64: response.data.pdf_base64,
                    individualName: individualName.trim(),
                    kycDocumentType: 'Enhanced Due Diligence (EDD)'
                });
                setShowReportUploadConfirmation(true);
                
            } else {
                throw new Error(response.data.error || 'PEP report generation failed');
            }
        } catch (error) {
            console.error('❌ PEP report error:', error);
            showError(getErrorMessage(error, 'KYC operation failed. Please try again.'));
        } finally {
            updateLoadingState('individualReport', false);
        }
    };

    // Function to get employee info by Django user ID
    const getEmployeeInfoByUserId = async (djangoUserId) => {
        if (!djangoUserId) return null;
        
        // Check cache first
        if (employeeInfoCache[djangoUserId]) {
            return employeeInfoCache[djangoUserId];
        }
        
        try {
            // For current user, use their own info
            if (currentUserInfo && currentUserInfo.user_id === parseInt(djangoUserId)) {
                const employeeInfo = {
                    first_name: currentUserInfo.first_name,
                    last_name: currentUserInfo.last_name,
                    position: currentUserInfo.position,
                    department: currentUserInfo.department,
                    office: currentUserInfo.office,
                    employee_id: currentUserInfo.employee_id
                };
                
                // Cache the result
                setEmployeeInfoCache(prev => ({
                    ...prev,
                    [djangoUserId]: employeeInfo
                }));
                
                return employeeInfo;
            } else {
                // For other users, return placeholder
                const placeholderInfo = {
                    first_name: 'User',
                    last_name: `ID ${djangoUserId}`,
                    position: 'Unknown',
                    department: 'Unknown',
                    office: 'Unknown',
                    employee_id: djangoUserId
                };
                
                return placeholderInfo;
            }
        } catch (error) {
            console.error(`Error fetching employee info for user ID ${djangoUserId}:`, error);
            return null;
        }
    };

    // Fetch KYC documents from DocuWare
    const fetchKycDocuments = async () => {
        if (!profileData?.id) return;
        
        setLoadingKycDocuments(true);
        setKycDocumentError(null);
        
        try {
            console.log('Fetching KYC documents for partner ID:', profileData.id);
            
            const response = await axiosInstance.get('/api/docuware/kyc-documents/documents/', {
                params: {
                    partner_id: profileData.id,
                    count: 100
                }
            });
            
            const documents = response.data.Items || [];
            setKycDocuments(documents);
            
            // Group documents by KYC document type
            const grouped = groupDocumentsByKycType(documents);
            setGroupedKycDocuments(grouped);

            // Bulk lookup AI display names; fall back silently to SUBJECT
            // for legacy documents that have no row in document_naming.
            try {
                const ids = documents.map(d => d?.Id).filter(Boolean).map(String);
                const map = await lookupNames(ids, 'kyc');
                setKycDisplayNames(map || {});
            } catch (lookupErr) {
                console.warn('AI display name lookup failed (non-blocking):', lookupErr);
            }
            
            console.log('Successfully fetched', documents.length, 'KYC documents');
            
        } catch (error) {
            console.error('Error fetching KYC documents:', error);
            setKycDocumentError(error.response?.data?.error || 'Failed to load KYC documents');
        } finally {
            setLoadingKycDocuments(false);
        }
    };

    // Group documents by KYC document type
    const groupDocumentsByKycType = (documents) => {
        const grouped = {};
        
        documents.forEach(doc => {
            const fields = doc.Fields || [];
            const kycTypeField = fields.find(f => f.FieldName === 'KYC_DOCUMENT_TYPE');
            const kycType = kycTypeField?.Item || 'Unknown';
            
            if (!grouped[kycType]) {
                grouped[kycType] = [];
            }
            grouped[kycType].push(doc);
        });
        
        return grouped;
    };

    // Duplicate detection helper functions (similar to HAMSTIR Builder)
    const checkForDuplicateFilename = (filename) => {
        console.log('🔍 FRONTEND: Checking for duplicate KYC filename:', filename);
        console.log('🔍 FRONTEND: Current KYC documents:', kycDocuments);
        
        if (!kycDocuments || kycDocuments.length === 0) {
            console.log('✅ FRONTEND: No existing KYC documents, no duplicates found');
            return { isDuplicate: false, existingDocument: null };
        }
        
        // Check if any existing document has the same filename in SUBJECT field
        const duplicateDocument = kycDocuments.find(doc => {
            const docInfo = getKycDocumentInfo(doc);
            const isDup = docInfo.filename === filename;
            console.log(`🔍 FRONTEND: Comparing "${docInfo.filename}" with "${filename}": ${isDup}`);
            return isDup;
        });
        
        if (duplicateDocument) {
            console.log('⚠️ FRONTEND: KYC duplicate found:', duplicateDocument);
            return { isDuplicate: true, existingDocument: duplicateDocument };
        }
        
        console.log('✅ FRONTEND: No KYC duplicates found');
        return { isDuplicate: false, existingDocument: null };
    };

    const generateIncrementedFilename = (originalFilename) => {
        console.log('🔢 FRONTEND: Generating incremented KYC filename for:', originalFilename);
        
        if (!kycDocuments || kycDocuments.length === 0) {
            console.log('✅ FRONTEND: No existing KYC documents, returning original filename');
            return originalFilename;
        }
        
        // Extract existing filenames from SUBJECT fields
        const existingFilenames = kycDocuments.map(doc => {
            const docInfo = getKycDocumentInfo(doc);
            return docInfo.filename;
        }).filter(Boolean); // Remove any null/undefined values
        
        console.log('📋 FRONTEND: Existing KYC filenames:', existingFilenames);
        
        // Parse the original filename to extract name and extension
        const lastDotIndex = originalFilename.lastIndexOf('.');
        const nameWithoutExt = lastDotIndex !== -1 ? originalFilename.substring(0, lastDotIndex) : originalFilename;
        const extension = lastDotIndex !== -1 ? originalFilename.substring(lastDotIndex) : '';
        
        console.log('📝 FRONTEND: Name without extension:', nameWithoutExt);
        console.log('📝 FRONTEND: Extension:', extension);
        
        // Start checking from (1) and increment until we find a unique name
        let counter = 1;
        let newFilename = `${nameWithoutExt} (${counter})${extension}`;
        
        while (existingFilenames.includes(newFilename)) {
            counter++;
            newFilename = `${nameWithoutExt} (${counter})${extension}`;
            console.log(`🔢 FRONTEND: Trying counter ${counter}: ${newFilename}`);
        }
        
        console.log('✅ FRONTEND: Generated unique KYC filename:', newFilename);
        return newFilename;
    };

    // Handle KYC file upload with document type and expiry date selection
    const handleKycFileUpload = async (kycDocumentType, file, expiryDate, namingPayload = null) => {
        // Validation
        if (!profileData?.id) {
            showError('Please save the partner profile first before uploading documents.');
            return;
        }

        if (!kycDocumentType) {
            showError('Please select a KYC document type.');
            return;
        }
        // Stash the AI naming payload on the file ref so the duplicate
        // handlers and the actual upload helper (performKycFileUpload)
        // can pick it up without changing every internal signature.
        // The persistDisplayName helper at the end of upload reads it
        // back and calls /api/document-naming/confirm/.
        file._namingPayload = namingPayload;
        
        // File size validation (30MB)
        const maxSizeInBytes = 30 * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            showError(`File size exceeds 30MB limit.`);
            return;
        }
        
        console.log('📤 FRONTEND: Starting KYC file upload process');
        console.log('📄 FRONTEND: File details:', { name: file.name, size: file.size, type: file.type });
        
        // Check for duplicate filename before proceeding with upload
        const duplicateCheck = checkForDuplicateFilename(file.name);
        
        if (duplicateCheck.isDuplicate) {
            console.log('⚠️ FRONTEND: KYC duplicate detected, showing modal');
            // Store the upload details for later processing
            setDuplicateFileInfo({
                kycDocumentType,
                file,
                expiryDate,
                existingDocument: duplicateCheck.existingDocument
            });
            setShowDuplicateModal(true);
            return; // Stop here and wait for user decision
        }
        
        // No duplicate found, proceed with normal upload
        console.log('✅ FRONTEND: No KYC duplicate found, proceeding with upload');
        await performKycFileUpload(kycDocumentType, file, expiryDate, file.name);
    };

    // Separate function to handle the actual KYC upload process
    const performKycFileUpload = async (kycDocumentType, file, expiryDate, finalFilename) => {
        console.log('🚀 FRONTEND: Performing KYC file upload with filename:', finalFilename);
        
        setUploadingKyc(true);
        
        try {
            const employeeId = currentUserInfo?.user_id || '1';
            const partnerName = profileData?.legal_entity_name || 'Unknown Partner';
            const subject = finalFilename; // Use the final filename (potentially renamed) as subject
            
            console.log('📋 FRONTEND: KYC Upload metadata:', {
                employeeId,
                partnerName,
                subject,
                kycDocumentType
            });
            
            // Step 1: Create document record
            const recordData = {
                partner_name: partnerName,
                employee_id: employeeId,
                partner_id: profileData?.id.toString(),
                subject: subject,
                kyc_document_type: kycDocumentType
            };
            
            // Add expiry date if provided
            if (expiryDate) {
                recordData.document_expiry_date = expiryDate;
            }
            
            console.log('📝 FRONTEND: Creating KYC document record...');
            const recordResponse = await axiosInstance.post('/api/docuware/kyc-documents/create-record/', recordData);
            
            if (!recordResponse.data.success || !recordResponse.data.document_id) {
                throw new Error('Failed to create document record');
            }
            
            const documentId = recordResponse.data.document_id;
            console.log('✅ FRONTEND: KYC Document record created with ID:', documentId);
            
            // Step 2: Upload file
            console.log('📤 FRONTEND: Uploading KYC file content...');
            const fileFormData = new FormData();
            fileFormData.append('file', file);
            
            await axiosInstance.post(`/api/docuware/kyc-documents/upload-file/${documentId}/`, fileFormData);
            
            // Persist the AI display name. The file upload already succeeded,
            // so a failure here must NOT block the success flow — surface a
            // warning toast and continue.
            const namingPayload = file?._namingPayload;
            if (namingPayload && documentId) {
                try {
                    await confirmName({
                        docuwareDocumentId: documentId,
                        module: 'kyc',
                        file,
                        typeDropdownValue: namingPayload.typeDropdownValue || '',
                        partnerName: namingPayload.partnerName || '',
                        kycSubtype: namingPayload.kycSubtype || '',
                        relatedEntityId: profileData?.id ? String(profileData.id) : '',
                        originalFilename: file?.name || finalFilename,
                        components: namingPayload.components || {},
                        aiSuggestion: namingPayload.aiSuggestion || {},
                        userOverrides: namingPayload.userOverrides || [],
                        extractionConfidence: namingPayload.extractionConfidence ?? null,
                    });
                } catch (err) {
                    console.warn('AI display name persist failed (non-blocking):', err);
                    showError('Document uploaded, but the AI name could not be saved — you can rename it in the Document Hub.');
                }
            }
            
            console.log('🎉 FRONTEND: KYC File upload completed successfully');
            showSuccess(`KYC document "${finalFilename}" uploaded successfully for ${kycDocumentType}!`);
            
            // Refresh document list
            if (showKycDocuments) {
                console.log('🔄 FRONTEND: Refreshing KYC document list...');
                setTimeout(() => {
                    fetchKycDocuments();
                }, 1000);
            }
            
        } catch (error) {
            console.error('❌ FRONTEND: KYC upload error:', error);
            const errorMessage = error.response?.data?.error || error.message;
            showError(`Upload failed: ${errorMessage}`);
        } finally {
            setUploadingKyc(false);
        }
    };

    // Duplicate modal handlers (similar to HAMSTIR Builder)
    const handleDuplicateKeepBoth = async () => {
        console.log('👥 FRONTEND: User chose to keep both KYC files');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate KYC file info available');
            return;
        }
        
        const { kycDocumentType, file, expiryDate } = duplicateFileInfo;
        const incrementedFilename = generateIncrementedFilename(file.name);
        
        console.log('📝 FRONTEND: Renaming KYC file to:', incrementedFilename);
        
        // Close modal and proceed with upload using incremented filename
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
        
        await performKycFileUpload(kycDocumentType, file, expiryDate, incrementedFilename);
    };

    const handleDuplicateReplace = async () => {
        console.log('🔄 FRONTEND: User chose to replace existing KYC file');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate KYC file info available');
            return;
        }
        
        const { kycDocumentType, file, expiryDate, existingDocument } = duplicateFileInfo;
        
        try {
            console.log('🗑️ FRONTEND: Deleting existing KYC document:', existingDocument.Id);
            
            // Delete the existing document first
            await axiosInstance.delete(`/api/docuware/kyc-documents/delete/${existingDocument.Id}/`);
            
            console.log('✅ FRONTEND: Existing KYC document deleted successfully');
            
            // Close modal and proceed with upload using original filename
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
            
            await performKycFileUpload(kycDocumentType, file, expiryDate, file.name);
            
        } catch (error) {
            console.error('❌ FRONTEND: Error deleting existing KYC document:', error);
            const errorMessage = error.response?.data?.error || error.message;
            showError(`Failed to delete existing document: ${errorMessage}`);
            
            // Close modal even if deletion failed
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
        }
    };

    const handleDuplicateCancel = () => {
        console.log('❌ FRONTEND: User cancelled KYC upload due to duplicate');
        
        // Simply close the modal and reset state
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
    };

    // Download KYC document
    const downloadKycDocument = async (documentId, filename) => {
        try {
            const response = await axiosInstance.get(
                `/api/docuware/kyc-documents/download/${documentId}/`,
                { responseType: 'blob' }
            );
            
            // Extract filename from headers if available
            let downloadFilename = filename;
            const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    downloadFilename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            if (!downloadFilename || downloadFilename === `KYC_Document_${documentId}`) {
                const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
                let extension = '';
                if (contentType.includes('pdf')) {
                    extension = '.pdf';
                } else if (contentType.includes('image')) {
                    extension = '.jpg';
                } else if (contentType.includes('document') || contentType.includes('word')) {
                    extension = '.doc';
                }
                downloadFilename = `KYC_Document_${documentId}${extension}`;
            }
            
            // Create blob URL and trigger download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = downloadFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            console.log(`Downloaded KYC document ${documentId} as "${downloadFilename}"`);
            
        } catch (error) {
            console.error('Error downloading KYC document:', error);
            const errorMessage = error.response?.data?.error || error.message;
            showError(`Failed to download document: ${errorMessage}`);
        }
    };

    // Delete KYC document
    const deleteKycDocument = async (documentId, filename) => {
        try {
            await axiosInstance.delete(`/api/docuware/kyc-documents/delete/${documentId}/`);
            
            // Refresh document list
            fetchKycDocuments();
            
            showSuccess(`Document "${filename}" deleted successfully`);
            
        } catch (error) {
            console.error('Error deleting KYC document:', error);
            const errorMessage = error.response?.data?.error || error.message;
            showError(`Failed to delete document: ${errorMessage}`);
        }
    };

    // Handle report upload confirmation - YES button
    const handleReportUploadConfirm = async () => {
        if (!pendingReportUpload) return;
        
        setUploadingReport(true);
        setShowReportUploadConfirmation(false);
        
        try {
            console.log('📤 Uploading report to DocuWare:', pendingReportUpload.fileName);
            
            // Convert base64 PDF to blob
            const binaryString = atob(pendingReportUpload.pdfBase64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
            const pdfFile = new File([pdfBlob], pendingReportUpload.fileName, { type: 'application/pdf' });
            
            // Upload to DocuWare using existing KYC upload functionality
            const employeeId = currentUserInfo?.user_id || '1';
            const partnerName = profileData?.legal_entity_name || 'Unknown Partner';
            
            // Step 1: Create document record
            const recordData = {
                partner_name: partnerName,
                employee_id: employeeId,
                partner_id: profileData?.id.toString(),
                subject: pendingReportUpload.fileName,
                kyc_document_type: pendingReportUpload.kycDocumentType
                // No expiry date for generated reports
            };
            
            const recordResponse = await axiosInstance.post('/api/docuware/kyc-documents/create-record/', recordData);
            
            if (!recordResponse.data.success || !recordResponse.data.document_id) {
                throw new Error('Failed to create document record');
            }
            
            const documentId = recordResponse.data.document_id;
            
            // Step 2: Upload file
            const fileFormData = new FormData();
            fileFormData.append('file', pdfFile);
            
            await axiosInstance.post(`/api/docuware/kyc-documents/upload-file/${documentId}/`, fileFormData);
            
            // Also trigger the download as the user expects
            const link = document.createElement('a');
            link.href = `data:application/pdf;base64,${pendingReportUpload.pdfBase64}`;
            link.download = pendingReportUpload.fileName;
            link.click();
            
            showSuccess(`${pendingReportUpload.type === 'sanctions' ? 'Sanctions' : 'PEP'} report uploaded to DocuWare and downloaded successfully!`);
            
            // Refresh document list
            if (showKycDocuments) {
                setTimeout(() => {
                    fetchKycDocuments();
                }, 1000);
            }
            
        } catch (error) {
            console.error('Report upload error:', error);
            const errorMessage = error.response?.data?.error || error.message;
            showError(`Failed to upload report to DocuWare: ${errorMessage}`);
            
            // Still download the file even if upload failed
            const link = document.createElement('a');
            link.href = `data:application/pdf;base64,${pendingReportUpload.pdfBase64}`;
            link.download = pendingReportUpload.fileName;
            link.click();
            
        } finally {
            setUploadingReport(false);
            setPendingReportUpload(null);
        }
    };

    // Handle report upload confirmation - NO button (just download)
    const handleReportUploadCancel = () => {
        if (!pendingReportUpload) return;
        
        // Just download the file
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${pendingReportUpload.pdfBase64}`;
        link.download = pendingReportUpload.fileName;
        link.click();
        
        showSuccess(`${pendingReportUpload.type === 'sanctions' ? 'Sanctions' : 'PEP'} report downloaded successfully`);
        
        // Clean up state
        setShowReportUploadConfirmation(false);
        setPendingReportUpload(null);
    };

    // Extract document metadata for display
    const getKycDocumentInfo = (document) => {
        const fields = document.Fields || [];
        const getFieldValue = (fieldName) => {
            const field = fields.find(f => f.FieldName === fieldName);
            return field?.Item || '';
        };
        
        const subject = getFieldValue('SUBJECT');
        const originalFilename = subject && subject !== 'KYC Document' ? subject : null;
        const fallbackFilename = `kyc_document_${document.Id}.pdf`;
        const filename = originalFilename || fallbackFilename;
        
        const dateUploaded = getFieldValue('DATE_UPLOADED');
        const kycDocumentType = getFieldValue('KYC_DOCUMENT_TYPE');
        const employeeId = getFieldValue('EMPLOYEE_ID');
        
        // Try multiple possible field names for expiry date
        let expiryDate = getFieldValue('DOCUMENT_EXPIRY_DATE') || 
                        getFieldValue('EXPIRY_DATE') || 
                        getFieldValue('EXPIRES_ON') || 
                        getFieldValue('VALID_UNTIL') ||
                        '';
                        
        // Clean up expiry date - handle empty strings and whitespace
        if (typeof expiryDate === 'string') {
            expiryDate = expiryDate.trim();
            if (expiryDate === '' || expiryDate === 'null' || expiryDate === 'undefined') {
                expiryDate = null;
            }
        }
        
        // Debug logging for expiry date issues
        console.log('=== KYC DOCUMENT INFO DEBUG ===');
        console.log('Document ID:', document.Id);
        console.log('All document fields:', fields);
        console.log('Field names available:', fields.map(f => f.FieldName));
        console.log('Raw expiry date field:', expiryDate);
        console.log('Expiry date type:', typeof expiryDate);
        console.log('Expiry date length:', expiryDate?.length);
        console.log('Cleaned expiry date:', expiryDate);
        console.log('=== END DEBUG ===');
        
        const docIdKey = document?.Id != null ? String(document.Id) : '';
        const aiRow = docIdKey ? kycDisplayNames[docIdKey] : null;

        return {
            filename,
            displayName: aiRow?.display_name || filename,
            aiDisplayName: aiRow?.display_name || '',
            nameRow: aiRow || null,
            onNameRow: (updated) => setKycDisplayNames((prev) => ({ ...(prev || {}), [docIdKey]: updated })),
            dateUploaded,
            kycDocumentType,
            fileSize: document.FileSize || 0,
            documentId: document.Id,
            employeeId,
            expiryDate
        };
    };

    // Calculate expiry status with color coding
    const getExpiryStatus = (expiryDateString) => {
        console.log('=== GET EXPIRY STATUS DEBUG ===');
        console.log('Input expiryDateString:', expiryDateString);
        console.log('Input type:', typeof expiryDateString);
        console.log('Input length:', expiryDateString?.length);
        console.log('Input truthy:', !!expiryDateString);
        
        // Handle null, undefined, empty string, or whitespace-only strings
        if (!expiryDateString || 
            (typeof expiryDateString === 'string' && expiryDateString.trim() === '')) {
            console.log('No expiry date provided, returning No Expiry');
            return { status: 'none', label: 'No Expiry', color: '#6b7280' };
        }
        
        try {
            let expiryDate;
            
            // Handle different date formats that DocuWare might send
            if (typeof expiryDateString === 'string') {
                const cleanDateString = expiryDateString.trim();
                console.log('Clean date string:', cleanDateString);
                
                // Check for .NET JSON date format first (/Date(timestamp)/)
                const netDateMatch = cleanDateString.match(/\/Date\((\d+)\)\//);
                if (netDateMatch) {
                    const timestamp = parseInt(netDateMatch[1], 10);
                    expiryDate = new Date(timestamp);
                    console.log('.NET JSON date parsed:', expiryDate);
                } else {
                    // Try parsing as-is first
                    expiryDate = new Date(cleanDateString);
                    console.log('First attempt parse result:', expiryDate);
                    console.log('First attempt isNaN:', isNaN(expiryDate.getTime()));
                    
                    // If that fails, try to handle timezone formats
                    if (isNaN(expiryDate.getTime())) {
                        console.log('First attempt failed, trying regex extraction');
                        // Handle ISO format with timezone (e.g., "2025-01-15T00:00:00+02:00")
                        const isoMatch = cleanDateString.match(/(\d{4}-\d{2}-\d{2})/);
                        console.log('ISO regex match:', isoMatch);
                        if (isoMatch) {
                            const dateOnly = isoMatch[1] + 'T00:00:00Z';
                            console.log('Constructed date string:', dateOnly);
                            expiryDate = new Date(dateOnly);
                            console.log('Second attempt parse result:', expiryDate);
                        }
                    }
                }
            } else {
                console.log('Non-string input, parsing directly');
                expiryDate = new Date(expiryDateString);
            }
            
            // Validate the parsed date
            if (isNaN(expiryDate.getTime())) {
                console.error('Invalid expiry date format after all attempts:', expiryDateString);
                console.log('=== END EXPIRY STATUS DEBUG (ERROR) ===');
                return { status: 'error', label: 'Invalid Date', color: '#6b7280' };
            }
            
            const now = new Date();
            console.log('Final parsed expiry date:', expiryDate);
            console.log('Current date:', now);
            
            // Calculate difference in days
            const diffTime = expiryDate.getTime() - now.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            console.log('Diff time (ms):', diffTime);
            console.log('Diff days:', diffDays);
            
            let result;
            if (diffDays < 0) {
                result = { 
                    status: 'expired', 
                    label: `Expired ${Math.abs(diffDays)} days ago`, 
                    color: '#dc2626' 
                };
            } else if (diffDays <= 30) {
                result = { 
                    status: 'expiring_soon', 
                    label: `Expires in ${diffDays} days`, 
                    color: '#f59e0b' 
                };
            } else {
                result = { 
                    status: 'valid', 
                    label: `Expires in ${diffDays} days`, 
                    color: '#059669' 
                };
            }
            
            console.log('Final result:', result);
            console.log('=== END EXPIRY STATUS DEBUG (SUCCESS) ===');
            return result;
        } catch (error) {
            console.error('Error parsing expiry date:', expiryDateString, error);
            console.log('=== END EXPIRY STATUS DEBUG (EXCEPTION) ===');
            return { status: 'error', label: 'Invalid Date', color: '#6b7280' };
        }
    };

    // Format expiry date for display
    const formatExpiryDate = (expiryDateString) => {
        console.log('=== FORMAT EXPIRY DATE DEBUG ===');
        console.log('Input for formatting:', expiryDateString);
        
        if (!expiryDateString) {
            console.log('No expiry date to format, returning null');
            return null;
        }
        
        try {
            let date;
            
            // Handle different date formats that DocuWare might send
            if (typeof expiryDateString === 'string') {
                const cleanDateString = expiryDateString.trim();
                console.log('Clean date string for formatting:', cleanDateString);
                
                // Check for .NET JSON date format first (/Date(timestamp)/)
                const netDateMatch = cleanDateString.match(/\/Date\((\d+)\)\//);
                if (netDateMatch) {
                    const timestamp = parseInt(netDateMatch[1], 10);
                    date = new Date(timestamp);
                    console.log('.NET JSON date formatted:', date);
                } else {
                    // Try parsing as-is first
                    date = new Date(cleanDateString);
                    console.log('First format attempt:', date);
                    
                    // If that fails, try to handle timezone formats
                    if (isNaN(date.getTime())) {
                        console.log('First format attempt failed, trying regex');
                        // Handle ISO format with timezone (e.g., "2025-01-15T00:00:00+02:00")
                        const isoMatch = cleanDateString.match(/(\d{4}-\d{2}-\d{2})/);
                        console.log('Format ISO match:', isoMatch);
                        if (isoMatch) {
                            date = new Date(isoMatch[1] + 'T00:00:00Z');
                            console.log('Second format attempt:', date);
                        }
                    }
                }
            } else {
                console.log('Non-string format input');
                date = new Date(expiryDateString);
            }
            
            // Validate the parsed date
            if (isNaN(date.getTime())) {
                console.error('Invalid date format for formatting:', expiryDateString);
                console.log('=== END FORMAT DEBUG (ERROR) ===');
                return 'Invalid Date';
            }
            
            const formatted = date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
            
            console.log('Final formatted date:', formatted);
            console.log('=== END FORMAT DEBUG (SUCCESS) ===');
            return formatted;
        } catch (error) {
            console.error('Error formatting expiry date:', expiryDateString, error);
            console.log('=== END FORMAT DEBUG (EXCEPTION) ===');
            return 'Invalid Date';
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        
        try {
            let date;
            
            if (typeof dateString === 'string') {
                // Check for .NET JSON date format
                const netDateMatch = dateString.match(/\/Date\((\d+)\)\//);
                if (netDateMatch) {
                    const timestamp = parseInt(netDateMatch[1], 10);
                    date = new Date(timestamp);
                } else {
                    // Handle regular ISO format dates
                    const cleanDateString = dateString.replace(/Z+$/, 'Z').trim();
                    date = new Date(cleanDateString);
                }
            } else {
                date = new Date(dateString);
            }
            
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (error) {
            console.error('Date formatting error:', error);
            return 'Invalid Date';
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Helper function to format screening results
    const formatScreeningResults = (results) => {
        // Handle both old format (results.data) and new format (direct results)
        const screeningData = results?.data || results;
        
        if (!screeningData) return null;
        
        const isSuspicious = screeningData.suspect === 1;
        const score = screeningData.score || 0;
        
        return (
            <div style={{
                marginTop: '1rem',
                border: `2px solid ${isSuspicious ? '#ef4444' : '#10b981'}`,
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                backgroundColor: 'white'
            }}>
                {/* Status Header */}
                <div style={{
                    background: isSuspicious 
                        ? 'linear-gradient(135deg, #ef4444, #dc2626)' 
                        : 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    padding: '0.75rem 1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div>
                        <h3 style={{ 
                            margin: 0, 
                            fontSize: '1rem', 
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            {isSuspicious ? '⚠ SUSPICIOUS MATCH' : '✓ NO SANCTIONS FOUND'}
                        </h3>
                        <p style={{ margin: '0.125rem 0 0 0', fontSize: '0.75rem', opacity: 0.9 }}>
                            Screening completed
                        </p>
                    </div>
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '6px',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '0.625rem', opacity: 0.9 }}>Score</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', lineHeight: '1.2' }}>{score}%</div>
                    </div>
                </div>
                
                {/* Screening Details */}
                <div style={{ padding: '1rem' }}>
                    {/* Screened Entity Info */}
                    <div style={{
                        backgroundColor: '#f9fafb',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        marginBottom: '0.75rem',
                        border: '1px solid #e5e7eb'
                    }}>
                        <h4 style={{ 
                            margin: '0 0 0.5rem 0', 
                            fontSize: '0.875rem', 
                            fontWeight: '600',
                            color: '#374151'
                        }}>
                            Screened Entity
                        </h4>
                        <div style={{ display: 'grid', gap: '0.25rem', fontSize: '0.8125rem' }}>
                            <div><strong>Name:</strong> {screeningData.name || 'N/A'}</div>
                            <div><strong>Address:</strong> {screeningData.address || 'N/A'}</div>
                        </div>
                    </div>
                    
                    {/* Suspicious Match Details */}
                    {isSuspicious && (
                        <div style={{
                            backgroundColor: '#fef2f2',
                            padding: '0.75rem',
                            borderRadius: '6px',
                            marginBottom: '0.75rem',
                            border: '1px solid #fecaca'
                        }}>
                            <h4 style={{ 
                                margin: '0 0 0.5rem 0', 
                                fontSize: '0.875rem', 
                                fontWeight: '600',
                                color: '#991b1b'
                            }}>
                                Sanctions List Match
                            </h4>
                            <div style={{ display: 'grid', gap: '0.25rem', fontSize: '0.8125rem', color: '#7f1d1d' }}>
                                {screeningData.entityId && (
                                    <div><strong>Entity ID:</strong> {screeningData.entityId}</div>
                                )}
                                {screeningData.hitListName && (
                                    <div><strong>Matched List:</strong> {screeningData.hitListName}</div>
                                )}
                                {screeningData.hitListOrg && (
                                    <div><strong>List Org:</strong> {screeningData.hitListOrg}</div>
                                )}
                                {screeningData.listsCreated && (
                                    <div><strong>Updated:</strong> {new Date(screeningData.listsCreated).toLocaleDateString()}</div>
                                )}
                            </div>
                            
                            {screeningData.adhocUrl && (
                                <div style={{ marginTop: '0.5rem' }}>
                                    <a 
                                        href={screeningData.adhocUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{
                                            color: '#dc2626',
                                            textDecoration: 'none',
                                            fontWeight: '600',
                                            fontSize: '0.8125rem',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.25rem'
                                        }}
                                    >
                                        Verify →
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Known Aliases */}
                    {isSuspicious && screeningData.entity?.names && screeningData.entity.names.length > 0 && (
                        <div style={{
                            backgroundColor: '#fffbeb',
                            padding: '0.75rem',
                            borderRadius: '6px',
                            marginBottom: '0.75rem',
                            border: '1px solid #fde68a',
                            maxHeight: '150px',
                            overflowY: 'auto'
                        }}>
                            <h4 style={{ 
                                margin: '0 0 0.5rem 0', 
                                fontSize: '0.875rem', 
                                fontWeight: '600',
                                color: '#92400e'
                            }}>
                                Known Aliases ({screeningData.entity.names.length})
                            </h4>
                            <ul style={{ 
                                margin: 0, 
                                paddingLeft: '1.25rem', 
                                fontSize: '0.8125rem',
                                color: '#78350f'
                            }}>
                                {screeningData.entity.names.map((alias, index) => (
                                    <li key={index} style={{ marginBottom: '0.125rem' }}>{alias}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    {/* Known Addresses */}
                    {isSuspicious && screeningData.entity?.addresses && screeningData.entity.addresses.length > 0 && (
                        <div style={{
                            backgroundColor: '#fef3c7',
                            padding: '0.75rem',
                            borderRadius: '6px',
                            border: '1px solid #fcd34d',
                            maxHeight: '150px',
                            overflowY: 'auto'
                        }}>
                            <h4 style={{ 
                                margin: '0 0 0.5rem 0', 
                                fontSize: '0.875rem', 
                                fontWeight: '600',
                                color: '#92400e'
                            }}>
                                Known Addresses ({screeningData.entity.addresses.length})
                            </h4>
                            <ul style={{ 
                                margin: 0, 
                                paddingLeft: '1.25rem', 
                                fontSize: '0.8125rem',
                                color: '#78350f'
                            }}>
                                {screeningData.entity.addresses.map((addr, index) => (
                                    <li key={index} style={{ marginBottom: '0.125rem' }}>{addr}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    {/* Clear Status Message */}
                    {!isSuspicious && (
                        <div style={{
                            backgroundColor: '#f0fdf4',
                            padding: '0.75rem',
                            borderRadius: '6px',
                            border: '1px solid #bbf7d0',
                            textAlign: 'center'
                        }}>
                            <p style={{ 
                                margin: 0, 
                                color: '#166534', 
                                fontSize: '0.8125rem',
                                lineHeight: '1.4'
                            }}>
                                No matches found. Entity cleared.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="crm-tab-panel crm-tab-modern">
            <CRMTabHero
                tone="emerald"
                icon={<TabIcons.ShieldCheck />}
                title="KYC & Compliance"
                description="KYC status, sanctions screening, PEP checks, and supporting compliance documents."
            />

            {/* KYC Onboarding Portal cases (per year) for this partner */}
            {!isNewProfile && profileData?.id && (
                <CRMKYCOnboardingSection profileId={profileData.id} />
            )}

            {/* Simplified KYC Status Section */}
            <div className="crm-form-section">
                <div className="crm-section-header">
                    <div className="crm-tab-section-head-titleblock">
                        <span className="crm-tab-section-head-icon" data-tone="emerald" aria-hidden="true">
                            <TabIcons.ShieldCheck />
                        </span>
                        <h2 className="crm-tab-section-head-title">KYC Status Overview</h2>
                    </div>
                    <div className="kyc-last-update">
                        <span className="last-update-label">Last KYC Update</span>
                        <span className="last-update-date">
                            {profileData?.updated_at_timestamp 
                                ? new Date(profileData.updated_at_timestamp).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })
                                : 'Not available'
                            }
                        </span>
                    </div>
                </div>
                
                <div className="crm-kyc-overview-grid">
                    <div className="crm-input-row">
                        <div className="crm-input-group half-width">
                            <label className="crm-input-label">KYC Status</label>
                            <div
                                className={`crm-input crm-status-readonly kyc-status-${formData.kyc_status_current?.toLowerCase()}`}
                                data-status={formData.kyc_status_current}
                                title="KYC status is set by the KYC review and cannot be edited here."
                            >
                                {formData.kyc_status_current || 'Pending'}
                            </div>
                            <span className="crm-kyc-readonly-hint">
                                Set by the KYC review — not editable here.
                            </span>
                            {isKycApprover && !isNewProfile && (() => {
                                // Show the button while EITHER half is unfinished.
                                // Gating on kyc_status_current alone hid it from
                                // exactly the partners that needed it: a partner
                                // whose KYC reads Approved but whose profile
                                // approval is still 'pending' has no Debtor/
                                // Creditor number and no approval-log row, and
                                // the screen offered no way to complete it.
                                // Two live paths produce that state -- rows
                                // written before kyc_status_current was locked,
                                // and a change request that adds a Debtor/
                                // Creditor role and demotes an approved profile
                                // back to pending.
                                const kycDone = formData.kyc_status_current === 'Approved';
                                const approvalDone = (
                                    formData.approval_status
                                    ?? profileData?.approval_status
                                ) === 'approved';
                                return !kycDone || !approvalDone;
                            })() && (
                                <button
                                    type="button"
                                    className="crm-btn-primary crm-kyc-approve-btn"
                                    style={{ marginTop: '8px' }}
                                    onClick={handleMarkKycApproved}
                                    // NOT disabled on document count. The server is the
                                    // only thing that knows whether a KYCGateBypass window
                                    // is open, so a zero-document precheck here would keep
                                    // the button dead during exactly the test phase the
                                    // bypass exists for. Let the server answer; it refuses
                                    // with a readable message when the requirement is live.
                                    disabled={markingKycApproved}
                                    title={
                                        formData.kyc_status_current === 'Approved'
                                            ? 'This partner\'s KYC is approved but its profile approval was never completed. Press to finish it and issue any missing Debtor/Creditor number.'
                                            : 'Tick KYC as done so business can continue.'
                                    }
                                >
                                    {markingKycApproved
                                        ? 'Marking…'
                                        : (formData.kyc_status_current === 'Approved'
                                            ? '✓ Complete Profile Approval'
                                            : '✓ Mark KYC Approved')}
                                </button>
                            )}
                        </div>

                        <div className="crm-input-group half-width">
                            <label className="crm-input-label">AML Risk Level</label>
                            <select
                                className={`crm-input crm-risk-select aml-risk-${formData.aml_risk_level_current?.toLowerCase()}`}
                                data-risk={formData.aml_risk_level_current}
                                value={formData.aml_risk_level_current}
                                onChange={(e) => handleInputChange('aml_risk_level_current', e.target.value)}
                            >
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                            </select>
                        </div>
                    </div>
                    {(() => {
                        const badge = getApprovalBadge(profileData || formData);
                        return (
                            <div className="crm-input-row">
                                <div className="crm-input-group half-width">
                                    <label className="crm-input-label">Profile Approval Status</label>
                                    <div
                                        className={`crm-input crm-status-readonly kyc-status-${badge.isGrandfathered || badge.status === 'approved' ? 'approved' : badge.status === 'rejected' ? 'flagged' : 'pending'}`}
                                        title={badge.title || 'Driven by the KYC review'}
                                    >
                                        {badge.label}
                                    </div>
                                    <span className="crm-kyc-readonly-hint">
                                        {badge.isGrandfathered
                                            ? badge.title
                                            : 'Driven by the KYC review — approve/reject the KYC case to change it.'}
                                    </span>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Swiss sanctions — SECO SESAM Section */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.ShieldCheck />}
                    iconTone="emerald"
                    title="Swiss sanctions — SECO SESAM"
                    actions={
                        <a
                            className="crm-seco-verify-link"
                            href="https://www.seco.admin.ch/seco/en/home/Aussenwirtschaftspolitik_Wirtschaftliche_Zusammenarbeit/Wirtschaftsbeziehungen/exportkontrollen-und-sanktionen/sanktionen-embargos.html"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Verify on official site ↗
                        </a>
                    }
                    help="Partners are screened in-house against the official SECO list (both annexes). The system finds potential matches — a person reviews them and records the check."
                />

                {/* Step 1 — Automated screening */}
                <div className="crm-seco-step">
                    <div className="crm-seco-step-head">
                        <span className="crm-seco-step-num">1</span>
                        <div className="crm-seco-step-titleblock">
                            <h3 className="crm-seco-step-title">Automated screening</h3>
                            <p className="crm-seco-step-help">
                                Runs nightly and at every recap. Matches this partner&apos;s
                                names and aliases against the imported list.
                            </p>
                        </div>
                    </div>

                    {(() => {
                        const latest = secoScreening?.latest;
                        // list_imported is False on the payload when no SECO list
                        // has ever been imported -- a stored "clear" from a run
                        // against an empty table must NOT read as green clear.
                        const listImported = secoScreening?.list_imported !== false;
                        const statusMap = {
                            clear: { label: 'Clear', cls: 'is-clear' },
                            possible_hits: { label: 'Possible hits', cls: 'is-possible' },
                            hits: { label: 'Hits', cls: 'is-hits' },
                        };
                        let chip;
                        if (!listImported) {
                            chip = { label: 'No list imported yet', cls: 'is-nolist' };
                        } else if (latest) {
                            chip = statusMap[latest.status] || { label: latest.status, cls: 'is-none' };
                        } else {
                            chip = { label: 'Not screened', cls: 'is-none' };
                        }
                        const screenedAt = latest?.screened_at
                            ? new Date(latest.screened_at).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short', day: 'numeric',
                            })
                            : null;
                        const listVersion = latest?.list_version_date
                            || secoScreening?.current_list_version_date;
                        const matchCount = latest?.hits?.length || 0;
                        return (
                            <div className="crm-seco-screening">
                                <div className="crm-seco-screen-head">
                                    <div className="crm-seco-screen-summary">
                                        <span className={`crm-seco-status-chip ${chip.cls}`}>{chip.label}</span>
                                        <span className="crm-seco-screen-meta">
                                            {loadingSecoScreening
                                                ? 'Loading…'
                                                : latest
                                                    ? `${matchCount} ${matchCount === 1 ? 'match' : 'matches'} · screened ${screenedAt}${listVersion ? ` · list version ${listVersion}` : ''}`
                                                    : 'Never screened'}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="crm-btn-secondary"
                                        onClick={handleRunSecoScreening}
                                        disabled={runningSecoScreening || !profileData?.id}
                                    >
                                        {runningSecoScreening ? 'Screening…' : 'Screen now'}
                                    </button>
                                </div>
                                {!listImported && !loadingSecoScreening && (
                                    <p className="crm-seco-nolist-help">
                                        Screening unavailable until the SECO list is downloaded —
                                        the nightly sync keeps retrying.
                                    </p>
                                )}
                            </div>
                        );
                    })()}
                </div>

                {/* Step 2 — Review potential matches */}
                <div className="crm-seco-step">
                    <div className="crm-seco-step-head">
                        <span className="crm-seco-step-num">2</span>
                        <div className="crm-seco-step-titleblock">
                            <h3 className="crm-seco-step-title">Review potential matches</h3>
                            <p className="crm-seco-step-help">
                                Dismiss a false positive with a note, or escalate — escalating
                                means compliance flags the partner, which blocks new recaps.
                            </p>
                        </div>
                    </div>

                    {(() => {
                        const hits = secoScreening?.latest?.hits || [];
                        // Only badge hits with a review made against the DISPLAYED
                        // (latest) screening -- a review from an older screening /
                        // list version must not leak onto a fresh run's hits.
                        const latestScreeningId = secoScreening?.latest?.id;
                        const reviewByTarget = {};
                        (secoScreening?.reviews || []).forEach((r) => {
                            if (latestScreeningId != null && r.screening !== latestScreeningId) return;
                            if (!reviewByTarget[r.target_ssid]) reviewByTarget[r.target_ssid] = r;
                        });
                        // Only surface hits that still need attention: unreviewed
                        // (dismiss/escalate actions) or escalated (badge). Dismissed
                        // false positives live in the audit trail below.
                        const reviewableHits = hits.filter(
                            (hit) => reviewByTarget[hit.target_ssid]?.decision !== 'dismissed'
                        );
                        if (!reviewableHits.length) {
                            return <p className="crm-seco-no-matches">No matches to review.</p>;
                        }
                        return (
                            <ul className="crm-seco-hits">
                                {reviewableHits.map((hit, idx) => {
                                    const review = reviewByTarget[hit.target_ssid];
                                    const annexes = (hit.sanctions_sets || [])
                                        .map((s) => s.name_eng)
                                        .filter(Boolean)
                                        .join(', ');
                                    return (
                                        <li key={`${hit.target_ssid}-${idx}`} className={`crm-seco-hit ${hit.tier === 'hit' ? 'is-hit' : 'is-possible'}`}>
                                            <div className="crm-seco-hit-main">
                                                <span className="crm-seco-hit-name">{hit.matched_name}</span>
                                                <span className="crm-seco-hit-score">
                                                    {Math.round((hit.score || 0) * 100)}%
                                                    {hit.tier === 'hit' ? ' match' : ' possible'}
                                                </span>
                                            </div>
                                            {hit.programme && (
                                                <div className="crm-seco-hit-meta">Programme: {hit.programme}</div>
                                            )}
                                            {annexes && (
                                                <div className="crm-seco-hit-meta">Annexes: {annexes}</div>
                                            )}
                                            {hit.justification && (
                                                <div className="crm-seco-hit-just">
                                                    {hit.justification.length > 220
                                                        ? `${hit.justification.slice(0, 220)}…`
                                                        : hit.justification}
                                                </div>
                                            )}
                                            {review ? (
                                                <div className={`crm-seco-review-badge is-${review.decision}`}>
                                                    {review.decision === 'dismissed' ? 'Dismissed' : 'Escalated'}
                                                    {review.reviewed_by_name ? ` by ${review.reviewed_by_name}` : ''}
                                                    {review.note ? ` — ${review.note}` : ''}
                                                </div>
                                            ) : (
                                                <div className="crm-seco-hit-actions">
                                                    <button
                                                        type="button"
                                                        className="crm-seco-hit-btn is-dismiss"
                                                        onClick={() => handleReviewSecoHit(hit, 'dismissed')}
                                                    >
                                                        Dismiss
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="crm-seco-hit-btn is-escalate"
                                                        onClick={() => handleReviewSecoHit(hit, 'escalated')}
                                                    >
                                                        Escalate
                                                    </button>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        );
                    })()}
                </div>

                {/* Step 3 — Record the check */}
                <div className="crm-seco-step">
                    <div className="crm-seco-step-head">
                        <span className="crm-seco-step-num">3</span>
                        <div className="crm-seco-step-titleblock">
                            <h3 className="crm-seco-step-title">Record the check</h3>
                            <p className="crm-seco-step-help">
                                Your audit evidence — record one at each material transaction
                                step. Recaps record one automatically; use this for manual
                                checks in between.
                            </p>
                        </div>
                    </div>

                    <div className="crm-seco-record">
                        <div className="crm-seco-annex-checks">
                            <label className="crm-seco-checkbox">
                                <input
                                    type="checkbox"
                                    checked={secoAnnexI}
                                    onChange={(e) => setSecoAnnexI(e.target.checked)}
                                />
                                <span>Annex I checked</span>
                            </label>
                            <label className="crm-seco-checkbox">
                                <input
                                    type="checkbox"
                                    checked={secoAnnexII}
                                    onChange={(e) => setSecoAnnexII(e.target.checked)}
                                />
                                <span>Annex II checked</span>
                            </label>
                        </div>
                        <div className="crm-input-row">
                            <div className="crm-input-group half-width">
                                <label className="crm-input-label">Transaction context</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    value={secoContext}
                                    onChange={(e) => setSecoContext(e.target.value)}
                                    placeholder="e.g. enquiry / deal reference"
                                />
                            </div>
                            <div className="crm-input-group half-width">
                                <label className="crm-input-label">Notes (optional)</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    value={secoNotes}
                                    onChange={(e) => setSecoNotes(e.target.value)}
                                    placeholder="Outcome / remarks"
                                />
                            </div>
                        </div>
                        <button
                            type="button"
                            className="crm-btn-primary crm-seco-record-btn"
                            onClick={handleRecordSecoCheck}
                            disabled={recordingSecoCheck || !secoAnnexI || !secoAnnexII}
                            title={(!secoAnnexI || !secoAnnexII) ? 'Both annexes must be checked' : 'Record this SECO SESAM check'}
                        >
                            {recordingSecoCheck ? 'Recording…' : 'Record check'}
                        </button>
                    </div>
                </div>

                {/* Audit trail */}
                <div className="crm-seco-history">
                    <h3 className="crm-seco-history-title">Audit trail</h3>
                    <p className="crm-seco-step-help crm-seco-history-help">
                        Every check on this partner — manual and automatic.
                    </p>
                    {loadingSecoChecks ? (
                        <p className="crm-seco-empty">Loading…</p>
                    ) : secoChecks.length === 0 ? (
                        <p className="crm-seco-empty">No SECO SESAM checks recorded yet.</p>
                    ) : (
                        <div className="crm-seco-table-wrap">
                            <table className="crm-seco-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>By</th>
                                        <th>Annexes</th>
                                        <th>Context</th>
                                        <th>Notes</th>
                                        <th>Screening</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {secoChecks.map((check) => (
                                        <tr key={check.id}>
                                            <td>
                                                {check.checked_at
                                                    ? new Date(check.checked_at).toLocaleString('en-US', {
                                                        year: 'numeric', month: 'short', day: 'numeric',
                                                        hour: '2-digit', minute: '2-digit'
                                                    })
                                                    : '—'}
                                            </td>
                                            <td>{check.checked_by_name || '—'}</td>
                                            <td>
                                                {check.annex_i && check.annex_ii
                                                    ? 'Annex I & II'
                                                    : [check.annex_i && 'Annex I', check.annex_ii && 'Annex II'].filter(Boolean).join(', ') || '—'}
                                            </td>
                                            <td>{check.context || '—'}</td>
                                            <td>{check.notes || '—'}</td>
                                            <td>
                                                {check.screening
                                                    ? `${check.screening_status || 'screened'}${check.list_version_date ? ` · list ${check.list_version_date}` : ''}`
                                                    : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Ultimate Beneficial Owner Information Section */}
            <div className="crm-form-section">
                <div className="crm-multi-profile-container">
                    <div className="crm-multi-profile-header">
                        <h2 className="crm-multi-profile-title">Ultimate Beneficial Owner Information</h2>
                        <button
                            type="button"
                            className="crm-add-profile-btn"
                            onClick={addUbo}
                        >
                            <span>+</span>
                            Add Ultimate Beneficial Owner
                        </button>
                    </div>

                    {formData.manual_ultimate_beneficial_owners?.length === 0 && (
                        <div className="crm-empty-state">
                            <div className="crm-empty-icon">👤</div>
                            <h3>No Ultimate Beneficial Owners added yet</h3>
                            <p>Add Ultimate Beneficial Owner details for compliance tracking (API integration pending).</p>
                        </div>
                    )}

                    {formData.manual_ultimate_beneficial_owners?.map((ubo, index) => (
                        <div key={index} className="crm-profile-block">
                            <div className="crm-profile-block-header">
                                <h3 className="crm-profile-block-title">
                                    Ultimate Beneficial Owner {index + 1}
                                    {ubo.ultimate_beneficial_owner_name && (
                                        <span className="crm-contact-name-preview"> - {ubo.ultimate_beneficial_owner_name}</span>
                                    )}
                                </h3>
                                <button
                                    type="button"
                                    className="crm-btn-danger crm-remove-btn"
                                    onClick={() => removeUbo(index)}
                                    title="Remove Ultimate Beneficial Owner"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="crm-input-row">
                                <div className="crm-input-group">
                                    <label className="crm-input-label required">Ultimate Beneficial Owner Name</label>
                                    <input
                                        type="text"
                                        className="crm-input"
                                        value={ubo.ultimate_beneficial_owner_name || ''}
                                        onChange={(e) => updateUboField(index, 'ultimate_beneficial_owner_name', e.target.value)}
                                        placeholder="Enter Ultimate Beneficial Owner full name"
                                    />
                                </div>
                            </div>

                            <div className="crm-input-row">
                                <div className="crm-input-group">
                                    <label className="crm-input-label required">Ultimate Beneficial Owner Address</label>
                                    <input
                                        type="text"
                                        className="crm-input"
                                        value={ubo.ultimate_beneficial_owner_address || ''}
                                        onChange={(e) => updateUboField(index, 'ultimate_beneficial_owner_address', e.target.value)}
                                        placeholder="Enter UBO address (street, city, postal code, country)"
                                    />
                                    <div className="crm-input-help-text">
                                        <small>
                                            Required for sanctions screening. The Humpert &amp; Kneer API requires
                                            both name and address to return a reliable result. Provide the UBO's
                                            known residential or registered address.
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div className="crm-input-row">
                                <div className="crm-input-group">
                                    <label className="crm-input-label">
                                        Sensitivity Level: {ubo.ultimate_beneficial_owner_sensitivity ?? DEFAULT_SCREENING_SENSITIVITY}%
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={ubo.ultimate_beneficial_owner_sensitivity ?? DEFAULT_SCREENING_SENSITIVITY}
                                        onChange={(e) => updateUboField(
                                            index,
                                            'ultimate_beneficial_owner_sensitivity',
                                            parseInt(e.target.value)
                                        )}
                                        className="crm-rating-slider"
                                    />
                                    <div className="crm-input-help-text">
                                        <small>Match threshold (0-100). Higher = stricter matching. Default: {DEFAULT_SCREENING_SENSITIVITY}%</small>
                                    </div>
                                </div>
                            </div>

                            <div className="crm-input-row">
                                <div className="crm-input-group">
                                    <div className="screening-buttons">
                                        <button
                                            type="button"
                                            className="crm-btn-primary"
                                            onClick={() => screenUbo(index)}
                                            disabled={
                                                uboScreeningLoading[index] ||
                                                !(ubo.ultimate_beneficial_owner_name || '').trim() ||
                                                !(ubo.ultimate_beneficial_owner_address || '').trim()
                                            }
                                        >
                                            {uboScreeningLoading[index] ? 'Screening...' : 'Run Sanctions Check'}
                                        </button>

                                        <button
                                            type="button"
                                            className="crm-btn-secondary"
                                            onClick={() => generateUboReport(index)}
                                            disabled={
                                                uboReportLoading[index] ||
                                                uploadingReport ||
                                                !(ubo.ultimate_beneficial_owner_name || '').trim() ||
                                                !(ubo.ultimate_beneficial_owner_address || '').trim()
                                            }
                                        >
                                            {uboReportLoading[index]
                                                ? 'Generating...'
                                                : uploadingReport
                                                    ? 'Uploading Report...'
                                                    : 'Generate Sanctions Report'}
                                        </button>
                                    </div>

                                    {ubo.sanction_screening_result && (
                                        <div style={{
                                            marginTop: '0.75rem',
                                            padding: '0.75rem',
                                            borderRadius: '6px',
                                            border: `2px solid ${ubo.sanction_screening_result.suspect === 1 ? '#ef4444' : '#10b981'}`,
                                            backgroundColor: ubo.sanction_screening_result.suspect === 1 ? '#fef2f2' : '#f0fdf4'
                                        }}>
                                            <div style={{
                                                fontWeight: 700,
                                                color: ubo.sanction_screening_result.suspect === 1 ? '#991b1b' : '#065f46'
                                            }}>
                                                {ubo.sanction_screening_result.suspect === 1
                                                    ? '⚠ SUSPICIOUS MATCH'
                                                    : '✓ NO SANCTIONS FOUND'}
                                                <span style={{
                                                    marginLeft: '0.75rem',
                                                    fontWeight: 500,
                                                    fontSize: '0.875rem'
                                                }}>
                                                    Score: {ubo.sanction_screening_result.score || 0}%
                                                </span>
                                            </div>
                                            {ubo.sanction_screening_result.suspect === 1 && (
                                                <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#7f1d1d' }}>
                                                    {ubo.sanction_screening_result.hitListName && (
                                                        <div><strong>Matched List:</strong> {ubo.sanction_screening_result.hitListName}</div>
                                                    )}
                                                    {ubo.sanction_screening_result.hitListOrg && (
                                                        <div><strong>Org:</strong> {ubo.sanction_screening_result.hitListOrg}</div>
                                                    )}
                                                    {ubo.sanction_screening_result.adhocUrl && (
                                                        <div style={{ marginTop: '0.25rem' }}>
                                                            <a
                                                                href={ubo.sanction_screening_result.adhocUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: '#dc2626', fontWeight: 600 }}
                                                            >
                                                                View on provider website →
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {ubo.sanction_screening_result.screening_date && (
                                                <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                                                    Last checked: {new Date(ubo.sanction_screening_result.screening_date).toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="crm-input-row">
                                <div className="crm-input-group">
                                    <label className="crm-input-label">Ultimate Beneficial Owner Details</label>
                                    <textarea
                                        className="crm-textarea"
                                        rows={4}
                                        value={ubo.ultimate_beneficial_owner_details || ''}
                                        onChange={(e) => updateUboField(index, 'ultimate_beneficial_owner_details', e.target.value)}
                                        placeholder="Enter detailed information about this Ultimate Beneficial Owner (ownership percentage, role, etc.)"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Sanctions Screening Section */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.AlertTriangle />}
                    iconTone="amber"
                    title="Sanctions Screening & Reports"
                />
                
                <div className="simple-screening-section">
                    <div className="crm-input-group">
                        <label className="crm-input-label">
                            Entity Name *
                            {profileData?.legal_entity_name && (
                                <i 
                                    className="fas fa-link" 
                                    style={{ 
                                        marginLeft: '0.5rem', 
                                        color: '#4a6b25', 
                                        fontSize: '0.875rem',
                                        cursor: 'help'
                                    }}
                                    title="Auto-populated from Partner Profile"
                                ></i>
                            )}
                        </label>
                        <input
                            type="text"
                            className="crm-input"
                            value={entityName}
                            onChange={(e) => setEntityName(e.target.value)}
                            placeholder="Enter legal entity name (e.g., REYCO GMBH GERMANY)"
                        />
                        <div className="crm-input-help-text">
                            <small>
                                {profileData?.legal_entity_name 
                                    ? 'Auto-populated from Partner Profile. You can edit if needed.'
                                    : 'Enter the legal entity name as it appears in official records.'}
                            </small>
                        </div>
                    </div>

                    <div className="crm-input-group">
                        <label className="crm-input-label">
                            Entity Address *
                            {profileData?.head_office_address_data?.formatted_address && (
                                <i 
                                    className="fas fa-link" 
                                    style={{ 
                                        marginLeft: '0.5rem', 
                                        color: '#4a6b25', 
                                        fontSize: '0.875rem',
                                        cursor: 'help'
                                    }}
                                    title="Auto-populated from Partner Profile Head Office Address"
                                ></i>
                            )}
                        </label>
                        <input
                            type="text"
                            className="crm-input"
                            value={entityAddress}
                            onChange={(e) => setEntityAddress(e.target.value)}
                            placeholder="Enter entity address (e.g., Nametkina Street 117420 Moscow RU)"
                        />
                        <div className="crm-input-help-text">
                            <small>
                                {profileData?.head_office_address_data?.formatted_address
                                    ? 'Auto-populated from Partner Profile Head Office Address. You can edit if needed.'
                                    : 'Full address required for sanctions screening. Include street, city, postal code, and country.'}
                            </small>
                        </div>
                    </div>

                    <div className="crm-input-group">
                        <label className="crm-input-label">Sensitivity Level: {sensitivity}%</label>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={sensitivity}
                            onChange={(e) => setSensitivity(parseInt(e.target.value))}
                            className="crm-rating-slider"
                        />
                        <div className="crm-input-help-text">
                            <small>Match threshold (0-100). Higher = stricter matching. Default: {DEFAULT_SCREENING_SENSITIVITY}%</small>
                        </div>
                    </div>
                    
                    <div className="screening-buttons">
                        <button
                            type="button"
                            className="crm-btn-primary"
                            onClick={handleEntitySearch}
                            disabled={isLoading.entitySearch || !entityName.trim() || !entityAddress.trim()}
                        >
                            {isLoading.entitySearch ? 'Searching...' : 'Run Sanctions Check'}
                        </button>
                        
                        <button
                            type="button"
                            className="crm-btn-secondary"
                            onClick={handleEntityReport}
                            disabled={isLoading.entityReport || !entityName.trim() || !entityAddress.trim() || uploadingReport}
                        >
                            {isLoading.entityReport ? 'Generating...' : uploadingReport ? 'Uploading Report...' : 'Generate Sanctions Report'}
                        </button>
                    </div>
                    
                    {entityResults && (
                        <div>
                            {entityResultsFromStorage && (
                                <div style={{
                                    backgroundColor: '#f0f9ff',
                                    border: '1px solid #0ea5e9',
                                    borderRadius: '6px',
                                    padding: '0.75rem',
                                    marginBottom: '1rem',
                                    fontSize: '0.875rem',
                                    color: '#0369a1'
                                }}>
                                    <strong>Displaying Previous Results:</strong> These are saved screening results from a previous check. Run a new search to update.
                                </div>
                            )}
                            {formatScreeningResults(entityResults)}
                        </div>
                    )}
                </div>
            </div>

            {/* PEP Section - Hidden (backend intact for future use) */}
            {false && (
                <div className="crm-form-section">
                    <CRMSectionHeader
                        icon={<TabIcons.UserCheck />}
                        iconTone="violet"
                        title="Politically Exposed Person Check & Reports"
                    />
                    <div className="simple-screening-section">
                        {/* PEP functionality preserved but hidden */}
                    </div>
                </div>
            )}

            {/* KYC Document Management Section */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.FileText />}
                    iconTone="blue"
                    title="KYC Document Management"
                />
                
                {/* Control Buttons */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '30px' 
                }}>
                    <div>
                        <label className="crm-input-label">KYC Documents (auto-loaded)</label>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            Documents are automatically loaded and grouped by document type with expiry tracking
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            type="button"
                            onClick={() => setShowKycDocuments(!showKycDocuments)}
                            style={{
                                background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)'
                            }}
                        >
                            {showKycDocuments ? 'Hide Documents' : 'View Documents'}
                        </button>
                        {showKycDocuments && (
                            <button
                                type="button"
                                onClick={fetchKycDocuments}
                                disabled={loadingKycDocuments}
                                style={{
                                    background: loadingKycDocuments ? '#6c757d' : 'linear-gradient(135deg, #5a7b35, #4a6b25)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: loadingKycDocuments ? 'not-allowed' : 'pointer',
                                    opacity: loadingKycDocuments ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                    boxShadow: loadingKycDocuments ? 'none' : '0 2px 4px rgba(74, 107, 37, 0.15)'
                                }}
                            >
                                {loadingKycDocuments ? 'Refreshing...' : '↻ Refresh'}
                            </button>
                        )}
                        {/* Upload button - always visible but disabled when profile not saved */}
                        <div style={{ position: 'relative' }}>
                            <button
                                type="button"
                                onClick={() => setShowKycUploadModal(true)}
                                disabled={uploadingKyc || isNewProfile || !profileData?.id}
                                style={{
                                    background: (uploadingKyc || isNewProfile || !profileData?.id) ? '#6c757d' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: (uploadingKyc || isNewProfile || !profileData?.id) ? 'not-allowed' : 'pointer',
                                    opacity: (uploadingKyc || isNewProfile || !profileData?.id) ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                    boxShadow: (uploadingKyc || isNewProfile || !profileData?.id) ? 'none' : '0 2px 4px rgba(245, 158, 11, 0.15)'
                                }}
                            >
                                {uploadingKyc ? 'Uploading...' : 'Upload KYC Document'}
                            </button>
                                                         {(isNewProfile || !profileData?.id) && (
                                 <div style={{
                                     position: 'absolute',
                                     top: '100%',
                                     left: '0',
                                     right: '0',
                                     marginTop: '0.25rem',
                                     fontSize: '0.75rem',
                                     color: '#6b7280',
                                     fontStyle: 'italic',
                                     wordWrap: 'break-word',
                                     lineHeight: '1.2'
                                 }}>
                                     Save profile first to upload documents
                                 </div>
                             )}
                        </div>
                        {/* Fill missing partner details from the KYC documents
                            already on file (portal + DocuWare). Suggest-and-confirm:
                            nothing is written until the user reviews the extracted
                            fields and saves the profile. */}
                        {onFillFromKycDocs && !isNewProfile && profileData?.id && (
                            <button
                                type="button"
                                onClick={onFillFromKycDocs}
                                style={{
                                    background: 'linear-gradient(135deg, #10b981, #059669)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.15)'
                                }}
                            >
                                ✨ Fill Missing Details from KYC Docs
                            </button>
                        )}
                    </div>
                </div>

                {/* Document Display */}
                {showKycDocuments && (
                    <div className="kyc-documents-container" style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        backgroundColor: '#f9fafb',
                        marginBottom: '1rem'
                    }}>
                        {loadingKycDocuments && (
                            <div style={{ 
                                padding: '2rem', 
                                textAlign: 'center',
                                color: '#6b7280'
                            }}>
                                Loading KYC documents...
                            </div>
                        )}

                        {kycDocumentError && (
                            <div style={{ 
                                padding: '1rem',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #ef4444',
                                borderRadius: '0.375rem',
                                color: '#dc2626',
                                margin: '1rem'
                            }}>
                                Error: {kycDocumentError}
                            </div>
                        )}

                        {!loadingKycDocuments && !kycDocumentError && Object.keys(groupedKycDocuments).length === 0 && (
                            <div style={{ 
                                padding: '2rem', 
                                textAlign: 'center',
                                color: '#6b7280'
                            }}>
                                No KYC documents found for this partner
                            </div>
                        )}

                        {!loadingKycDocuments && !kycDocumentError && Object.keys(groupedKycDocuments).map(kycType => (
                            <KycDocumentGroup
                                key={kycType}
                                kycDocumentType={kycType}
                                documents={groupedKycDocuments[kycType]}
                                onDownload={downloadKycDocument}
                                onDelete={(documentId, filename) => {
                                    setDocumentToDelete({ id: documentId, filename });
                                    setShowDeleteConfirmation(true);
                                }}
                                getDocumentInfo={getKycDocumentInfo}
                                formatDate={formatDate}
                                formatFileSize={formatFileSize}
                                getEmployeeInfoByUserId={getEmployeeInfoByUserId}
                                getExpiryStatus={getExpiryStatus}
                                formatExpiryDate={formatExpiryDate}
                            />
                        ))}
                    </div>
                )}
                
                {/* Upload Modal */}
                <KYCUploadModal
                    isOpen={showKycUploadModal}
                    onClose={() => setShowKycUploadModal(false)}
                    kycDocumentTypes={KYC_DOCUMENT_TYPES}
                    onDocumentSelect={handleKycFileUpload}
                    uploading={uploadingKyc}
                    profileId={profileData?.id}
                    partnerName={profileData?.legal_entity_name || ''}
                />

                {/* Delete Confirmation Popup */}
                {showDeleteConfirmation && documentToDelete && (
                    <ConfirmationPopup
                        message={`Are you sure you want to delete "${documentToDelete.filename}"? This action cannot be undone.`}
                        onConfirm={() => {
                            deleteKycDocument(documentToDelete.id, documentToDelete.filename);
                            setShowDeleteConfirmation(false);
                            setDocumentToDelete(null);
                        }}
                        onCancel={() => {
                            setShowDeleteConfirmation(false);
                            setDocumentToDelete(null);
                        }}
                    />
                )}

                {/* Duplicate File Detection Modal */}
                {showDuplicateModal && duplicateFileInfo && (
                    <div className="confirmation-popup-overlay">
                        <div className="confirmation-popup">
                            <h3 style={{ 
                                margin: '0 0 1rem 0', 
                                color: '#374151',
                                fontSize: '1.25rem',
                                fontWeight: '600'
                            }}>
                                Duplicate File Detected
                            </h3>
                            <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>
                                A file with the name <strong>"{duplicateFileInfo.file.name}"</strong> already exists in this partner's KYC documents.
                            </p>
                            <p style={{ margin: '0 0 1.5rem 0', color: '#374151' }}>
                                What would you like to do?
                            </p>
                            <div style={{ 
                                display: 'inline-flex', 
                                gap: '0.5rem', 
                                justifyContent: 'flex-end' 
                            }}>
                                <button
                                    type="button"
                                    onClick={handleDuplicateKeepBoth}
                                    disabled={uploadingKyc}
                                    style={{
                                        background: uploadingKyc ? '#6c757d' : 'linear-gradient(135deg, #10b981, #059669)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '0.75rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: uploadingKyc ? 'not-allowed' : 'pointer',
                                        opacity: uploadingKyc ? 0.6 : 1,
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    Keep Both
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDuplicateReplace}
                                    disabled={uploadingKyc}
                                    style={{
                                        background: uploadingKyc ? '#6c757d' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '0.75rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: uploadingKyc ? 'not-allowed' : 'pointer',
                                        opacity: uploadingKyc ? 0.6 : 1,
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    Replace
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDuplicateCancel}
                                    disabled={uploadingKyc}
                                    style={{
                                        background: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '0.75rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: uploadingKyc ? 'not-allowed' : 'pointer',
                                        opacity: uploadingKyc ? 0.4 : 1,
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Report Upload Confirmation Popup */}
                {showReportUploadConfirmation && pendingReportUpload && (
                    <ConfirmationPopup
                        message={
                            <div style={{ textAlign: 'left' }}>
                                <h3 style={{ 
                                    margin: '0 0 1rem 0', 
                                    color: '#374151',
                                    fontSize: '1.25rem',
                                    fontWeight: '600'
                                }}>
                                    Upload Report to KYC Documents
                                </h3>
                                <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>
                                    <strong>{pendingReportUpload.type === 'sanctions' ? 'Sanctions' : 'PEP'} report</strong> has been generated successfully.
                                </p>
                                <div style={{ 
                                    backgroundColor: '#f3f4f6', 
                                    padding: '0.75rem', 
                                    borderRadius: '6px',
                                    marginBottom: '1rem'
                                }}>
                                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem' }}>
                                        <strong>File:</strong> {pendingReportUpload.fileName}
                                    </p>
                                    <p style={{ margin: '0', fontSize: '0.875rem' }}>
                                        <strong>Will be uploaded to:</strong> {pendingReportUpload.kycDocumentType}
                                    </p>
                                </div>
                                <p style={{ margin: '0', color: '#374151', fontSize: '0.875rem' }}>
                                    Would you like to upload this report to the KYC document management system?
                                </p>
                                <p style={{ margin: '0.5rem 0 0 0', color: '#6b7280', fontSize: '0.75rem' }}>
                                    The file will be downloaded regardless of your choice.
                                </p>
                            </div>
                        }
                        onConfirm={handleReportUploadConfirm}
                        onCancel={handleReportUploadCancel}
                        confirmText={uploadingReport ? "Uploading..." : "Yes, Upload to DocuWare"}
                        cancelText="No, Just Download"
                        confirmDisabled={uploadingReport}
                    />
                )}
            </div>


        </div>
    );
};

// Component to display KYC document group with expiry indicators
const KycDocumentGroup = ({ 
    kycDocumentType, 
    documents, 
    onDownload, 
    onDelete, 
    getDocumentInfo, 
    formatDate, 
    formatFileSize,
    getEmployeeInfoByUserId,
    getExpiryStatus,
    formatExpiryDate
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    
    return (
        <div className="kyc-document-group">
            <div 
                className="kyc-group-header"
                onClick={() => setIsExpanded(!isExpanded)}
                style={{ 
                    cursor: 'pointer',
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                    borderRadius: '6px',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)',
                    transition: 'all 0.2s ease'
                }}
            >
                <h4 style={{ margin: 0, color: 'white', fontWeight: '600', fontSize: '1rem' }}>
                    {kycDocumentType} Documents ({documents.length})
                </h4>
                <span style={{ 
                    fontSize: '1rem', 
                    color: 'white',
                    fontWeight: '600',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                }}>
                    ⌄
                </span>
            </div>
            
            {isExpanded && (
                <div className="kyc-group-documents">
                    {documents.map(document => (
                        <KycDocumentItem
                            key={document.Id}
                            document={document}
                            onDownload={onDownload}
                            onDelete={onDelete}
                            getDocumentInfo={getDocumentInfo}
                            formatDate={formatDate}
                            formatFileSize={formatFileSize}
                            getEmployeeInfoByUserId={getEmployeeInfoByUserId}
                            getExpiryStatus={getExpiryStatus}
                            formatExpiryDate={formatExpiryDate}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// Individual KYC document item with expiry status
const KycDocumentItem = ({ 
    document, 
    onDownload, 
    onDelete, 
    getDocumentInfo, 
    formatDate, 
    formatFileSize, 
    getEmployeeInfoByUserId,
    getExpiryStatus,
    formatExpiryDate 
}) => {
    const [employeeInfo, setEmployeeInfo] = useState(null);
    const [loadingEmployee, setLoadingEmployee] = useState(false);
    
    const docInfo = getDocumentInfo(document);
    const expiryStatus = getExpiryStatus(docInfo.expiryDate);
    
    // Fetch employee info when component mounts
    useEffect(() => {
        const fetchEmployeeInfo = async () => {
            if (!docInfo.employeeId) return;
            
            setLoadingEmployee(true);
            try {
                const empInfo = await getEmployeeInfoByUserId(docInfo.employeeId);
                setEmployeeInfo(empInfo);
            } catch (error) {
                console.error('Error fetching employee info for document:', error);
            } finally {
                setLoadingEmployee(false);
            }
        };
        
        fetchEmployeeInfo();
    }, [docInfo.employeeId, getEmployeeInfoByUserId]);
    
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.75rem',
            marginBottom: '0.5rem',
            backgroundColor: '#ffffff',
            borderRadius: '6px',
            border: '1px solid #e5e7eb'
        }}>
            <div style={{ flex: 1 }}>
                <div style={{ 
                    fontWeight: 'bold', 
                    color: '#495057',
                    marginBottom: '0.25rem',
                    fontSize: '0.95rem'
                }} title={docInfo.displayName || docInfo.filename}>
                    {docInfo.displayName || docInfo.filename}
                </div>
                <SuggestedName row={docInfo.nameRow} onChanged={docInfo.onNameRow} />
                {docInfo.aiDisplayName && docInfo.aiDisplayName !== docInfo.filename && (
                    <div style={{
                        fontSize: '0.75rem',
                        color: '#888',
                        marginBottom: '0.25rem',
                        wordBreak: 'break-all'
                    }} title={docInfo.filename}>
                        {docInfo.filename}
                    </div>
                )}

                {/* Expiry Status Badge */}
                {docInfo.expiryDate && 
                 typeof docInfo.expiryDate === 'string' && 
                 docInfo.expiryDate.trim() !== '' && 
                 expiryStatus.status !== 'none' && (
                    <div style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: expiryStatus.color + '20',
                        color: expiryStatus.color,
                        border: `1px solid ${expiryStatus.color}`,
                        marginBottom: '0.5rem'
                    }}>
                        {expiryStatus.label} • {formatExpiryDate(docInfo.expiryDate)}
                    </div>
                )}
                
                <div style={{ 
                    fontSize: '0.85rem', 
                    color: '#6c757d',
                    marginBottom: '0.25rem'
                }}>
                    Uploaded: {formatDate(docInfo.dateUploaded)} | Size: {formatFileSize(docInfo.fileSize)}
                </div>
                
                {/* Employee Information Section */}
                <div style={{ 
                    fontSize: '0.85rem', 
                    color: '#495057',
                    marginTop: '0.5rem',
                    padding: '0.4rem 0.6rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                }}>
                    <strong>Uploaded by:</strong>
                    {loadingEmployee ? (
                        <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>Loading...</span>
                    ) : employeeInfo ? (
                        <div style={{ marginTop: '0.25rem' }}>
                            <div><strong>{employeeInfo.first_name} {employeeInfo.last_name}</strong></div>
                            {employeeInfo.position && (
                                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                    {employeeInfo.position}
                                    {employeeInfo.department && ` • ${employeeInfo.department}`}
                                    {employeeInfo.office && ` • ${employeeInfo.office}`}
                                </div>
                            )}
                            <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                Employee ID: {employeeInfo.employee_id}
                            </div>
                        </div>
                    ) : (
                        <span style={{ marginLeft: '0.5rem', fontStyle: 'italic', color: '#6c757d' }}>
                            Employee info not available
                        </span>
                    )}
                </div>
                
                <div style={{ 
                    fontSize: '0.8rem', 
                    color: '#6c757d',
                    marginTop: '0.25rem'
                }}>
                    Document ID: {docInfo.documentId}
                </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                <button
                    onClick={() => onDownload(docInfo.documentId, docInfo.filename)}
                    style={{
                        background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minWidth: '80px',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)'
                    }}
                >
                    Download
                </button>
                <button
                    onClick={() => onDelete(docInfo.documentId, docInfo.filename)}
                    style={{
                        background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minWidth: '80px',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(220, 38, 38, 0.15)'
                    }}
                >
                    Delete
                </button>
            </div>
        </div>
    );
};

export default CRMKYCComplianceTab; 